import axios from 'axios';
import type { TimecardAbsencePay } from '../types';
import { LAST_SESSION_ACTIVITY_AT_KEY } from '../constants/session';
import { pinUrlToLocalhostIfLocalUi } from '../utils/tenantHost';
import { readCachedTenantForIdentifier, saveCachedTenant } from './loginPreferences';
import { normalizeCurrencyLabel } from '../utils/currencyText';
import { applyCompanyDateFormat } from '../utils/companyDateFormat';
import { Employee, EmployeeHierarchyResponse, OrganizationHierarchyResponse, Location, Position, TimeStation, Schedule, PayrollDepartment, PayrollEmploymentType, Role, Permission, BreakTimeSettings, SystemSetting, TrainingType, CompanySettings, PublicHoliday, CreateEmployeeRequest, UpdateEmployeeRequest, CreateLocationRequest, CreatePositionRequest, CreateTimeStationRequest, CreateScheduleRequest, CreatePayrollDepartmentRequest, CreateRoleRequest, UpdateRoleRequest, CreateBreakTimeSettingsRequest, WorkingTimeRestRule, UpsertWorkingTimeRestRuleRequest, CreateSystemSettingRequest, CreateTrainingTypeRequest, UpdateTrainingTypeRequest, CreateCompanySettingsRequest, UpdateCompanySettingsRequest, CreatePublicHolidayRequest, UpdatePublicHolidayRequest, EmployeeLicense, CreateEmployeeLicenseRequest, UpdateEmployeeLicenseRequest, PaymentType, PaymentFrequency, AssignLaborCostByOption, WorkingDay, PayRecord, CreatePayRecordRequest, UpdatePayRecordRequest, FeedbackRecord, CreateFeedbackRecordRequest, UpdateFeedbackRecordRequest, EmployeeNote, CreateEmployeeNoteRequest, UpdateEmployeeNoteRequest, TrainingRecord, CreateTrainingRecordRequest, UpdateTrainingRecordRequest, TrainingRecordCycle, CreateTrainingRecordCycleRequest, UpdateTrainingRecordCycleRequest, HolidayRequest, CreateHolidayRequestRequest, UpdateHolidayRequestRequest, BlockedDate, CreateBlockedDateRequest, UpdateBlockedDateRequest, Incident, CreateIncidentRequest, UpdateIncidentRequest, Shift, CreateShiftRequest, ShiftColleaguesResponse, UpdateShiftRequest, ShiftGroup, CreateShiftGroupRequest, UpdateShiftGroupRequest, ShiftTemplate, CreateShiftTemplateRequest, UpdateShiftTemplateRequest, AttendanceRecord, CreateAttendanceRecordRequest, UpdateAttendanceRecordRequest, AttendanceImportColumnMapping, AttendanceImportRowInput, AttendanceImportValidateResponse, AttendanceImportCommitResponse, AttendanceImportBulkDeleteResponse, AttendanceImportBatchDetail, AttendanceImportHistoryPage, ScheduleShiftImportColumnMapping, ScheduleShiftImportRowInput, ScheduleShiftImportValidateResponse, ScheduleShiftImportCommitResponse, ScheduleShiftImportBulkDeleteResponse, ScheduleShiftImportBatchDetail, ScheduleShiftImportHistoryPage, ScclImportColumnMapping, ScclImportRowInput, ScclImportValidateResponse, ScclImportCommitResponse, ScclImportHistoryPage, ScclImportBatchDetail, Task, MyTask, CreateTaskRequest, UpdateTaskRequest, TaskConversation, AddConversationRequest, TaskConversationReaction, SubTask, CreateSubTaskRequest, UpdateSubTaskRequest, SubTaskConversation, StaffMessage, MyStaffMessage, MessageInboxItem, MessageInboxUnreadSummary, CreateMessageRequest, CreateDirectMessageRequest, CreateGroupChatRequest, UpdateMessageRequest, MessageConversation, PayModel, CreatePayModelRequest, UpdatePayModelRequest, LeaveType, LeaveLedger, LeaveLedgerReportEntry, CreateLeaveTypeRequest, UpdateLeaveTypeRequest, LeavePolicy, CreateLeavePolicyRequest, UpdateLeavePolicyRequest, CreateLeavePolicyTypeRequest, UpdateLeavePolicyTypeRequest, EmployeeLeaveBalance, EmployeeLeaveTypeInfo, CreateEmployeeLeaveBalanceRequest, UpdateEmployeeLeaveBalanceRequest, EmployeeSickLeaveProfile, CreateEmployeeSickLeaveProfileRequest, UpdateEmployeeSickLeaveProfileRequest, SickLeaveRequest, CreateSickLeaveRequestRequest, UpdateSickLeaveRequestRequest, ApproveSickLeaveRequestRequest, RejectSickLeaveRequestRequest, EmployeePublicHoliday, CreateEmployeePublicHolidayRequest, UpdateEmployeePublicHolidayRequest, CheckEligibilityRequest, PublicHolidayPolicy, CreatePublicHolidayPolicyRequest, UpdatePublicHolidayPolicyRequest, RecurringJob, PublicHolidayJobRun, PublicHolidayJobLog, Job, CreateJobRequest, UpdateJobRequest, JobSchedule, CreateJobScheduleRequest, UpdateJobScheduleRequest, JobStep, CreateJobStepRequest, UpdateJobStepRequest, JobStepCondition, CreateJobStepConditionRequest, JobRunHistory, JobStepRunHistory, Timecard, CreateTimecardRequest, UpdateTimecardDayRequest, AuditLogConfig, AuditLogConfigFieldRequest, AuditLogEvent, PayrollRun, CreatePayrollRunRequest, PayrollRunPreviewDto, PayrollRunAuditDto, SchedulingBootstrapResponse, SchedulingWeekViewResponse, SchedulingPublicationStatus, JobApplicant, JobApplicantUpsertRequest, JobApplicantListValue, JobApplicantImportValidateRequest, JobApplicantImportValidateResponse, JobApplicantImportCommitResponse, JobApplicantBulkCreateResponse, ShiftMarketplaceRequest, CreateMarketplaceRequestPayload, LoginResponse, LoginSuccessResponse, EmployeeMfaStatus, UpdateMyEmployeeDetailsRequest, ApprovalInboxItem, AuthorityDelegationRow, DelegationHistoryResult, UnavailabilityRequest, UpsertUnavailabilityRequest, UnavailabilityClashShift, ScheduleExpectedCount, UpsertScheduleExpectedCountRequest, ScheduleExpectedCountWeek, UpsertScheduleExpectedCountWeekRequest, SchedulePlaceholder, ScheduleTextReminders, SaveScheduleTextRemindersRequest, MyReportTypeDef, MyReportRunListItem, MyReportRunDetail, MyReportTemplate } from '../types';

const NO_API_URL_MSG = 'API URL must come from tenant (DB). Resolve tenant first.';
const BOOTSTRAP_FALLBACK = 'http://localhost:5026/api'; // Main API fallback
const TENANT_DISCOVERY_FALLBACK = 'http://127.0.0.1:5027/api'; // IPv4 — Windows localhost→::1 can stall ~10s

function resolveLocalApiUrl(url: string): string {
  return pinUrlToLocalhostIfLocalUi(url) || url;
}

/** API URL: tenant from DB (localStorage) or bootstrap for tenant/resolve. */
export const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const tenantUrl = localStorage.getItem('tenantApiUrl');
    if (tenantUrl) return resolveLocalApiUrl(tenantUrl);
    const bootstrap = process.env.REACT_APP_API_URL?.trim();
    if (bootstrap) return resolveLocalApiUrl(bootstrap);
    return BOOTSTRAP_FALLBACK;
  }
  const bootstrap = process.env.REACT_APP_API_URL?.trim();
  if (bootstrap) return bootstrap;
  return BOOTSTRAP_FALLBACK;
};

/** Set API base URL from tenant (DB). Call after tenant resolve. Pass null to clear on logout. */
export const setApiBaseUrl = (url: string | null): void => {
  if (url) {
    const pinned = resolveLocalApiUrl(url);
    const normalized = pinned.endsWith('/api') ? pinned : `${pinned.replace(/\/$/, '')}/api`;
    localStorage.setItem('tenantApiUrl', normalized);
    api.defaults.baseURL = normalized;
  } else {
    localStorage.removeItem('tenantApiUrl');
    const bootstrap = resolveLocalApiUrl(process.env.REACT_APP_API_URL?.trim() || BOOTSTRAP_FALLBACK);
    api.defaults.baseURL = bootstrap;
  }
};

const API_BASE_URL = getApiBaseUrl();

/** Base URL without /api (for SignalR hubs, attachment URLs) */
export const getApiBaseUrlNoPath = (): string => {
  const url = getApiBaseUrl();
  return url.endsWith('/api') ? url.replace(/\/api\/?$/, '') : url.replace(/\/$/, '');
};

/** Full URL for profile image. Handles relative paths (e.g. /uploads/profile-images/...) and absolute URLs.
 * cacheBust: optional timestamp/version to append as ?t= to bypass browser cache when image is updated (e.g. after face re-enrollment). */
export const getProfileImageUrl = (profileImageUrl: string | null | undefined, cacheBust?: string | number | null): string | null => {
  if (!profileImageUrl?.trim()) return null;
  let url: string;
  if (profileImageUrl.startsWith('http://') || profileImageUrl.startsWith('https://')) {
    url = profileImageUrl;
  } else {
    const base = getApiBaseUrlNoPath();
    url = `${base}${profileImageUrl.startsWith('/') ? '' : '/'}${profileImageUrl}`;
  }
  // Never modify presigned S3 URLs (adding query params breaks the signature and returns 403).
  const isPresignedAwsUrl =
    /^https?:\/\//i.test(url) &&
    (url.includes('X-Amz-Signature=') || url.includes('X-Amz-Credential=') || url.includes('X-Amz-Algorithm='));

  if (!isPresignedAwsUrl && cacheBust != null && cacheBust !== '') {
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}t=${encodeURIComponent(String(cacheBust))}`;
  }
  return url;
};

/** Stored path or presigned URL from API — do not prepend the API host to https URLs. */
export const getUploadedFileUrl = (filePath: string | null | undefined): string => {
  if (!filePath?.trim()) return '';
  const path = filePath.trim();
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = getApiBaseUrlNoPath();
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

/** Authenticated playback URL for message conversation voice attachments. */
export function getMessageConversationAttachmentPlaybackUrl(
  messageId: number,
  conversationId: number,
  attachmentId: number
): string {
  const base = getApiBaseUrl().replace(/\/?$/, '');
  return `${base}/messages/${messageId}/conversations/${conversationId}/attachments/${attachmentId}/media`;
}

/** Authenticated playback URL for task conversation voice attachments — use with fetch + Bearer; HTML &lt;audio&gt; cannot authorize S3. */
export function getTaskConversationAttachmentPlaybackUrl(
  taskId: number,
  conversationId: number,
  attachmentId: number
): string {
  const base = getApiBaseUrl().replace(/\/?$/, '');
  return `${base}/tasks/${taskId}/conversations/${conversationId}/attachments/${attachmentId}/media`;
}

/** Authenticated playback URL for sub-task conversation voice attachments */
export function getSubTaskConversationAttachmentPlaybackUrl(
  taskId: number,
  subTaskId: number,
  conversationId: number,
  attachmentId: number
): string {
  const base = getApiBaseUrl().replace(/\/?$/, '');
  return `${base}/tasks/${taskId}/subtasks/${subTaskId}/conversations/${conversationId}/attachments/${attachmentId}/media`;
}

console.log('[API] Base URL initialized:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Avoid hung requests after idle tabs / sleeping connections (browser may stall TCP for minutes).
  timeout: 45000,
});

// Request interceptor: attach JWT and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, clear tokens and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Do not treat MFA challenge failures as session expiry (those endpoints also return 401).
    const reqUrl = String(error.config?.url || '');
    if (
      error.response?.status === 401 &&
      !reqUrl.includes('/auth/login') &&
      !reqUrl.includes('/auth/mfa/') &&
      !reqUrl.includes('/tenant/')
    ) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('employee');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantApiUrl');
      localStorage.removeItem('locationsCache');
      localStorage.removeItem(LAST_SESSION_ACTIVITY_AT_KEY);
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        // Store current path so user can be redirected back after re-login
        const path = window.location.pathname + window.location.search;
        if (path && path !== '/' && path !== '/login') {
          sessionStorage.setItem('loginRedirect', path);
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Tenant API (public - no auth required)
export interface TenantResolveResponse {
  tenantId: number;
  tenantName: string;
  tenantCode: string;
  isActive: boolean;
  /** Optional API base URL for this tenant. When set, used for all subsequent API calls. */
  apiUrl?: string | null;
  branding?: { logoUrl?: string; primaryColor?: string };
}

/** Tenant Discovery API base URL. Prefer IPv4 locally — Windows `localhost` → ::1 can stall ~10s. */
const getTenantDiscoveryBaseUrl = (): string => {
  const raw = resolveLocalApiUrl(process.env.REACT_APP_TENANT_DISCOVERY_URL?.trim() || TENANT_DISCOVERY_FALLBACK);
  try {
    const parsed = new URL(raw);
    if (parsed.hostname === 'localhost') {
      parsed.hostname = '127.0.0.1';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return raw;
  }
};

const tenantResolveInflight = new Map<string, Promise<TenantResolveResponse>>();

async function fetchTenantResolve(identifier: string): Promise<TenantResolveResponse> {
  const existing = tenantResolveInflight.get(identifier.toLowerCase());
  if (existing) return existing;

  const pending = (async () => {
    const baseUrl = getTenantDiscoveryBaseUrl();
    const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl.replace(/\/$/, '')}/api`;
    const response = await axios.get(`${apiBase}/tenant/resolve`, {
      params: { identifier },
    });
    const tenant = response.data as TenantResolveResponse;
    if (tenant?.tenantId && tenant.tenantCode && tenant.apiUrl?.trim()) {
      saveCachedTenant({
        tenantId: tenant.tenantId,
        tenantCode: tenant.tenantCode,
        tenantName: tenant.tenantName,
        apiUrl: tenant.apiUrl,
      });
    }
    return tenant;
  })().finally(() => {
    tenantResolveInflight.delete(identifier.toLowerCase());
  });

  tenantResolveInflight.set(identifier.toLowerCase(), pending);
  return pending;
}

export const tenantApi = {
  resolve: async (identifier: string): Promise<TenantResolveResponse> => {
    const value = identifier?.trim();
    if (!value) {
      throw new Error('Company identifier is required.');
    }
    const cached = readCachedTenantForIdentifier(value);
    if (cached) {
      void fetchTenantResolve(value).catch(() => undefined);
      return cached;
    }
    return fetchTenantResolve(value);
  },
};

// Auth API
export const authApi = {
  login: async (tenantId: number, email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await api.post('/auth/login', { tenantId, email, password });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Login failed');
      }
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        throw new Error(`Cannot connect to API server. Please ensure the API server is running.`);
      }
      throw new Error(error?.message || 'Network error. Please check your connection.');
    }
  },

  mfa: {
    sendCode: async (challengeToken: string, method?: string) => {
      const response = await api.post('/auth/mfa/send-code', { challengeToken, method });
      return response.data as { message: string; method?: string; destination?: string };
    },
    verify: async (challengeToken: string, code: string, method?: string): Promise<LoginSuccessResponse> => {
      const response = await api.post('/auth/mfa/verify', { challengeToken, code, method });
      return response.data;
    },
    setupAuthenticatorStart: async (challengeToken: string) => {
      const response = await api.post('/auth/mfa/setup/authenticator/start', { challengeToken });
      return response.data as { secret: string; otpauthUrl: string; method: string };
    },
    setupConfirm: async (challengeToken: string, method: string, code: string): Promise<LoginSuccessResponse> => {
      const response = await api.post('/auth/mfa/setup/confirm', { challengeToken, method, code });
      return response.data;
    },
    getMe: async (): Promise<EmployeeMfaStatus> => {
      const response = await api.get('/auth/mfa/me');
      return response.data;
    },
    startMyAuthenticator: async () => {
      const response = await api.post('/auth/mfa/me/authenticator/start');
      return response.data as { secret: string; otpauthUrl: string };
    },
    sendMyCode: async (method: string) => {
      const response = await api.post('/auth/mfa/me/send-code', { method });
      return response.data as { message: string; destination?: string };
    },
    enableMe: async (method: string, code: string): Promise<EmployeeMfaStatus> => {
      const response = await api.post('/auth/mfa/me/enable', { method, code });
      return response.data;
    },
    disableMe: async (): Promise<EmployeeMfaStatus> => {
      const response = await api.post('/auth/mfa/me/disable');
      return response.data;
    },
    getEmployee: async (employeeId: number): Promise<EmployeeMfaStatus> => {
      const response = await api.get(`/auth/mfa/employee/${employeeId}`);
      return response.data;
    },
    exemptEmployee: async (employeeId: number, hours: number, note: string): Promise<EmployeeMfaStatus> => {
      const response = await api.post(`/auth/mfa/employee/${employeeId}/exempt`, { hours, note });
      return response.data;
    },
    clearExempt: async (employeeId: number): Promise<EmployeeMfaStatus> => {
      const response = await api.post(`/auth/mfa/employee/${employeeId}/clear-exempt`);
      return response.data;
    },
    resetEmployee: async (employeeId: number): Promise<EmployeeMfaStatus> => {
      const response = await api.post(`/auth/mfa/employee/${employeeId}/reset`);
      return response.data;
    },
  },

  logout: async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    const apiBase = getApiBaseUrl();

    // Clear local session immediately so Sign out never hangs on a slow/hung API call.
    localStorage.removeItem('authToken');
    localStorage.removeItem('employee');
    localStorage.removeItem('tenantId');
    localStorage.removeItem(LAST_SESSION_ACTIVITY_AT_KEY);

    if (!token || !apiBase) return;

    // Best-effort server audit only; short timeout; must not block UI navigation.
    try {
      await axios.post(
        `${apiBase.replace(/\/$/, '')}/auth/logout`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 4000,
        }
      );
    } catch {
      // ignore — local session already cleared
    }
  },

  forgotPassword: {
    request: async (tenantId: number, email: string): Promise<{ email: string; passwordQuestion: string; hasQuestion: boolean }> => {
      try {
        const response = await api.post('/forgot-password/request', { tenantId, email });
        return response.data;
      } catch (error: any) {
        if (error?.response) {
          const message = error.response.data?.message || 'Failed to request password reset';
          throw new Error(message);
        } else if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNREFUSED' || error?.message?.includes('Network Error')) {
          throw new Error('Network error. Cannot reach API server. Please check your connection.');
        } else {
          throw new Error(error?.message || 'Network error. Please check your connection.');
        }
      }
    },

    reset: async (tenantId: number, email: string, passwordAnswer: string): Promise<{ message: string; emailSent: boolean }> => {
      try {
        const response = await api.post('/forgot-password/reset', { tenantId, email, passwordAnswer });
        return response.data;
      } catch (error: any) {
        if (error.response) {
          throw new Error(error.response.data?.message || 'Failed to reset password');
        }
        throw new Error('Network error. Please check your connection.');
      }
    },
  },

  updateAccount: async (data: {
    email: string;
    newPassword?: string;
    passwordQuestion?: string;
    passwordAnswer?: string;
  }): Promise<{ message: string; employee: Employee }> => {
    try {
      const response = await api.put('/auth/account', data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to update account');
      }
      throw new Error('Network error. Please check your connection.');
    }
  },

  getSession: async (): Promise<{
    permissionCodes: string[];
    hasSuperAdminRole: boolean;
    managesTeam?: boolean;
    securityCommandControlLogEnabled: boolean;
    mandatoryScheduleInputEnabled: boolean;
    healthcareComplianceEnabled: boolean;
    healthcareNurseBandingPayrollEnabled: boolean;
    mainLocationId?: number | null;
    assignedLocations?: Location[];
  }> => {
    const response = await api.get('/auth/session');
    const assigned = normalizeLocationsList(response.data?.assignedLocations);
    if (assigned.length > 0) {
      seedMyLocationsCache(assigned);
    }
    return { ...response.data, assignedLocations: assigned };
  },
};

/** Dashboard: licenses + training in one round-trip (no per-employee N+1). */
export interface DashboardPeriodMetricsDto {
  periodType: string;
  periodStart: string;
  periodEnd: string;
  locationId?: number | null;
  status: string;
  computedAt?: string | null;
  fromCache: boolean;
  kpis: {
    planCost: number;
    actualCost: number;
    planVsActualPct: number;
    scheduledHours: number;
    actualHours: number;
    attendancePct: number;
    lateCount: number;
    earlyCount: number;
    otHours: number;
  };
  series: {
    byDay: Array<{ date: string; dateLabel: string; planned: number; actual: number }>;
    byLocation: Array<{ name: string; value: number; percent: number; color?: string }>;
    byDepartment: Array<{ name: string; value: number; percent: number; color?: string }>;
  };
  leave: { annual: number; sick: number; other: number };
  payrollReadiness: {
    percent: number;
    approved: number;
    pending: number;
    rejected: number;
    total: number;
  };
  counts: { employees: number; shifts: number; timecards: number };
}

export interface DashboardPeriodMetricsResponse {
  current: DashboardPeriodMetricsDto;
  previous?: DashboardPeriodMetricsDto | null;
}

export interface DashboardSummary {
  counts: {
    totalEmployees: number;
    mainEmployeesAtLocation: number;
    sharedSubEmployeesCount: number;
    presentToday: number;
    pendingHolidayRequests: number;
  };
  absentToday: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    date: string;
  }>;
  missedClockout: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    recordId: number;
    date: string;
    clockInTime: string;
    hoursClocked: string;
  }>;
  visaAlerts: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    visaNumber: string;
    visaExpiryDate: string;
    daysUntilExpiry: number;
  }>;
  licenseExpired: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    licenseId: number;
    licenseType?: string | null;
    licenseNumber: string;
    expiryDate: string;
    daysSinceExpiry: number;
    renewalUploaded?: boolean;
  }>;
  licenseExpiringSoon: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    licenseId: number;
    licenseType?: string | null;
    licenseNumber: string;
    expiryDate: string;
    daysUntilExpiry: number;
    renewalUploaded?: boolean;
  }>;
  upcomingTraining: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    recordId: number;
    dueDate: string;
    label: string;
    isOverdue: boolean;
  }>;
  lateClockInToday?: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    minutesLate: number;
    shiftStart: string;
    actualClockIn: string;
    schedule: string;
  }>;
  earlyClockOutToday?: Array<{
    employeeId: number;
    firstName: string;
    lastName: string;
    minutesEarly: number;
    shiftEnd: string;
    actualClockOut: string;
    schedule: string;
  }>;
  pendingHolidayRequests?: Array<{
    requestId?: number;
    employeeId: number;
    firstName: string;
    lastName: string;
    startDate: string;
    endDate: string;
    leaveType: string;
  }>;
}

export const dashboardApi = {
  getSummary: async (locationId?: number | null): Promise<DashboardSummary> => {
    const params =
      locationId != null && locationId !== 0 ? { locationId } : undefined;
    const response = await api.get('/dashboard/summary', { params });
    return response.data;
  },
  getAlertsData: async (): Promise<{
    licenses: EmployeeLicense[];
    trainingRecords: TrainingRecord[];
    trainingTypes: TrainingType[];
  }> => {
    const response = await api.get('/dashboard/alerts-data');
    return response.data;
  },
  getSpotlight: async (params: {
    filter: string;
    fromDate: string;
    toDate: string;
    locationId?: number | null;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<{
    filter: string;
    fromDate: string;
    toDate: string;
    locationId?: number | null;
    limit: number;
    rows: Array<{
      rank: number;
      employeeId: number;
      firstName: string;
      lastName: string;
      name: string;
      role: string;
      profileImageUrl?: string;
      updatedAt?: string;
      primaryValue: string;
      primaryNumeric: number;
      secondaryText?: string;
      documentLabel?: string;
      href: string;
    }>;
  }> => {
    const response = await api.get('/dashboard/spotlight', {
      params: {
        filter: params.filter,
        fromDate: params.fromDate,
        toDate: params.toDate,
        locationId:
          params.locationId != null && params.locationId !== 0 ? params.locationId : undefined,
        limit: params.limit ?? 5,
        // Bust HTTP intermediaries; spotlight is computed live and must not stick at 100%.
        _: Date.now(),
      },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      signal: params.signal,
    });
    return response.data;
  },
  getSpotlightDetails: async (params: {
    employeeId: number;
    filter: string;
    fromDate: string;
    toDate: string;
  }): Promise<{
    employeeId: number;
    name: string;
    role: string;
    profileImageUrl?: string;
    updatedAt?: string;
    filter: string;
    fromDate: string;
    toDate: string;
    summaryPrimary: string;
    summarySecondary: string;
    details: Array<{
      date: string;
      label: string;
      status: string;
      statusLabel: string;
      scheduledIn?: string;
      scheduledOut?: string;
      actualIn?: string;
      actualOut?: string;
      lateMinutes?: number;
      earlyMinutes?: number;
      primaryText: string;
      secondaryText: string;
      highlight?: boolean;
    }>;
  }> => {
    const response = await api.get('/dashboard/spotlight/details', {
      params: {
        employeeId: params.employeeId,
        filter: params.filter,
        fromDate: params.fromDate,
        toDate: params.toDate,
      },
    });
    return response.data;
  },

  getPeriodMetrics: async (params: {
    periodType: 'Week' | 'Month';
    periodStart: string;
    locationId?: number | null;
    includePrevious?: boolean;
    bypassCache?: boolean;
  }): Promise<DashboardPeriodMetricsResponse> => {
    const locKey =
      params.locationId != null && params.locationId !== 0 ? String(params.locationId) : 'all';
    const key = `period-metrics:${params.periodType}:${params.periodStart}:${locKey}:${params.includePrevious ?? true}`;
    if (params.bypassCache) periodMetricsCache.invalidate(key);
    return periodMetricsCache.get(key, async () => {
      const loc =
        params.locationId != null && params.locationId !== 0 ? params.locationId : undefined;
      if (params.bypassCache) {
        try {
          await api.post('/dashboard/period-metrics/rebuild', null, {
            params: {
              periodType: params.periodType,
              periodStart: params.periodStart,
              locationId: loc,
            },
          });
        } catch {
          // Rebuild is best-effort; fall through to GET so Refresh still returns data.
        }
      }
      const response = await api.get('/dashboard/period-metrics', {
        params: {
          periodType: params.periodType,
          periodStart: params.periodStart,
          locationId: loc,
          includePrevious: params.includePrevious ?? true,
          fresh: params.bypassCache ? true : undefined,
        },
        headers: params.bypassCache
          ? { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
          : undefined,
      });
      const data = response.data as DashboardPeriodMetricsResponse;
      // Computing is a placeholder until the worker finishes — never cache it as a hit.
      if (String(data?.current?.status ?? '').toLowerCase() === 'computing') {
        periodMetricsCache.invalidate(key);
      }
      return data;
    });
  },
};

/** Empty arrays can be a premature/poisoned fetch; never persist them as a hit. */
function shouldPersistCachedValue<T>(value: T): boolean {
  return !Array.isArray(value) || value.length > 0;
}

/** In-flight + result cache so parallel callers share one HTTP request per key. */
function createKeyedReadCache<T>() {
  const results = new Map<string, T>();
  const inflight = new Map<string, Promise<T>>();
  const generations = new Map<string, number>();
  return {
    get: (key: string, fetcher: () => Promise<T>): Promise<T> => {
      const hit = results.get(key);
      if (hit !== undefined) return Promise.resolve(hit);
      let pending = inflight.get(key);
      if (!pending) {
        const gen = generations.get(key) ?? 0;
        pending = fetcher()
          .then((value) => {
            if ((generations.get(key) ?? 0) === gen && shouldPersistCachedValue(value)) {
              results.set(key, value);
            }
            return value;
          })
          .finally(() => {
            if (inflight.get(key) === pending) {
              inflight.delete(key);
            }
          });
        inflight.set(key, pending);
      }
      return pending;
    },
    invalidate: (key: string) => {
      generations.set(key, (generations.get(key) ?? 0) + 1);
      results.delete(key);
      inflight.delete(key);
    },
    clear: () => {
      // Bump gens so in-flight fetches started before clear cannot overwrite with stale data.
      const keys = Array.from(
        new Set<string>([
          ...Array.from(results.keys()),
          ...Array.from(inflight.keys()),
          ...Array.from(generations.keys()),
        ])
      );
      keys.forEach((key) => {
        generations.set(key, (generations.get(key) ?? 0) + 1);
      });
      results.clear();
      inflight.clear();
    },
  };
}

function createSingletonReadCache<T>() {
  let result: T | undefined;
  let inflight: Promise<T> | null = null;
  let generation = 0;
  return {
    get: (fetcher: () => Promise<T>): Promise<T> => {
      if (result !== undefined) return Promise.resolve(result);
      if (!inflight) {
        const gen = generation;
        const pending = fetcher()
          .then((value) => {
            if (gen === generation && shouldPersistCachedValue(value)) {
              result = value;
            }
            return value;
          })
          .finally(() => {
            if (inflight === pending) {
              inflight = null;
            }
          });
        inflight = pending;
      }
      return inflight;
    },
    seed: (value: T) => {
      generation += 1;
      inflight = null;
      result = shouldPersistCachedValue(value) ? value : undefined;
    },
    invalidate: () => {
      generation += 1;
      result = undefined;
      inflight = null;
    },
  };
}

const employeeByIdCache = createKeyedReadCache<Employee>();
const rolesListCache = createSingletonReadCache<Role[]>();
const companySettingsCache = createSingletonReadCache<CompanySettings>();
const employeesListCache = createKeyedReadCache<Employee[]>();
const employeePickerCache = createSingletonReadCache<EmployeePickerRow[]>();
const holidayAdminBootstrapCache = createSingletonReadCache<HolidayAdminBootstrap>();

export interface EmployeePickerRow {
  id: number;
  firstName: string;
  lastName: string;
  isActive: boolean;
  mainLocationId: number;
  mainPositionId?: number | null;
  mainPositionTitle?: string | null;
  subLocationIds: number[];
  subPositionIds?: number[];
  scheduleIds: number[];
  scheduleNames?: string[];
  allowAdditiveHourlyPay?: boolean;
  isManager?: boolean;
}

export interface HolidayAdminBootstrap {
  holidayRequests: HolidayRequest[];
  employees: EmployeePickerRow[];
  blockedDates: BlockedDate[];
  leaveTypes: LeaveType[];
}

/** Map picker row to minimal Employee shape for pages that only need names + profile ids. */
export function employeePickerToEmployee(row: EmployeePickerRow): Employee {
  return {
    id: row.id,
    firstName: row.firstName,
    lastName: row.lastName,
    isActive: row.isActive,
    payroll: {
      allowAdditiveHourlyPay: row.allowAdditiveHourlyPay ?? false,
    },
    profile: {
      mainLocationId: row.mainLocationId,
      mainPositionId: row.mainPositionId ?? undefined,
      positionName: row.mainPositionTitle ?? undefined,
      subLocationIds: row.subLocationIds,
      subPositionIds: row.subPositionIds ?? [],
      scheduleIds: row.scheduleIds,
      isManager: row.isManager ?? false,
    },
  } as Employee & { scheduleNames?: string[] };
}

export type EmployeeListQueryOptions = {
  includePayroll?: boolean;
  presignImages?: boolean;
  page?: number;
  pageSize?: number;
};

export type EmployeeListPageFilterCondition = {
  field: string;
  value?: string | number | boolean | number[] | string[] | null;
  valueFrom?: string | number | null;
  valueTo?: string | number | null;
};

export type EmployeeListPageQuery = {
  status?: 'Active' | 'InActive' | 'All';
  locationId?: number | null;
  q?: string;
  sortColumn?: 'name' | 'position' | 'location' | null;
  sortDirection?: 'asc' | 'desc';
  presignImages?: boolean;
  filters?: EmployeeListPageFilterCondition[];
};

export type EmployeeListPageResult = {
  items: Employee[];
  totalCount: number;
};

export type EmployeeListPageFilterOptions = {
  locations: Array<{ id: number; name: string }>;
  positions: Array<{ id: number; title: string }>;
  payrollDepartments: Array<{ id: number; name: string; isActive: boolean }>;
  schedules: Array<{ id: number; name: string }>;
  roles: Array<{ id: number; name: string }>;
  paymentTypes: Array<{ id: number; name: string; isActive: boolean }>;
  paymentFrequencies: Array<{ id: number; name: string; isActive: boolean }>;
  leavePolicies: Array<{ leavePolicyId: number; policyName: string; isActive: boolean }>;
  payrollEmploymentTypes: Array<{ id: number; displayName: string; code: string }>;
};

function employeeListCacheKey(options?: EmployeeListQueryOptions): string {
  const includePayroll = options?.includePayroll ?? false;
  const presignImages = options?.presignImages ?? false;
  const page = options?.page;
  const pageSize = options?.pageSize;
  if (page != null && pageSize != null) {
    return `list:${includePayroll}:${presignImages}:p${page}:s${pageSize}`;
  }
  return `list:${includePayroll}:${presignImages}`;
}
const locationsListCache = createSingletonReadCache<Location[]>();
const myLocationsListCache = createSingletonReadCache<Location[]>();
const positionsListCache = createSingletonReadCache<Position[]>();
const holidayRequestsListCache = createKeyedReadCache<HolidayRequest[]>();
const systemSettingByKeyCache = createKeyedReadCache<SystemSetting>();
const shiftsListCache = createKeyedReadCache<Shift[]>();
const attendanceListCache = createKeyedReadCache<AttendanceRecord[]>();
const periodMetricsCache = createKeyedReadCache<DashboardPeriodMetricsResponse>();

function paramsCacheKey(prefix: string, params?: Record<string, unknown>): string {
  if (!params) return prefix;
  const entries = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  return entries.length === 0 ? prefix : `${prefix}:${JSON.stringify(entries)}`;
}

export function seedMyLocationsCache(locations: Location[]): void {
  if (locations.length > 0) {
    myLocationsListCache.seed(locations);
  }
}

/** Clear session read caches (e.g. on logout). */
export const clearApiReadCaches = (): void => {
  employeeByIdCache.clear();
  rolesListCache.invalidate();
  companySettingsCache.invalidate();
  employeesListCache.clear();
  employeePickerCache.invalidate();
  holidayAdminBootstrapCache.invalidate();
  locationsListCache.invalidate();
  myLocationsListCache.invalidate();
  positionsListCache.invalidate();
  holidayRequestsListCache.clear();
  systemSettingByKeyCache.clear();
  shiftsListCache.clear();
  attendanceListCache.clear();
  periodMetricsCache.clear();
};

/** Bust employee picker/list caches after idle wake so pages don't reuse stale in-flight requests. */
export const invalidateEmployeeListReadCaches = (): void => {
  employeesListCache.clear();
  employeePickerCache.invalidate();
};

/** Bust week-scoped shift/attendance reads (timecards sidebar activity + hours). */
export const invalidateWeekShiftAttendanceReadCaches = (): void => {
  shiftsListCache.clear();
  attendanceListCache.clear();
};

/** Bust permission-related reads after roles/settings/profile change. */
export const invalidatePermissionsReadCaches = (employeeId?: number): void => {
  if (employeeId != null) employeeByIdCache.invalidate(String(employeeId));
  rolesListCache.invalidate();
  companySettingsCache.invalidate();
  try {
    sessionStorage.removeItem('staffly:permissionsCache');
    localStorage.removeItem('staffly:permissionsLocalCache');
  } catch {
    /* ignore */
  }
};

/** Bust location list reads after create/update/delete (matches LocationContext localStorage key). */
export const invalidateLocationsReadCaches = (): void => {
  locationsListCache.invalidate();
  try {
    localStorage.removeItem('locationsCache');
  } catch (_) {}
};

function normalizeLocationIsActive(value: unknown): boolean {
  if (value === false || value === 0 || value === 'false' || value === 'False') return false;
  if (value === true || value === 1 || value === 'true' || value === 'True') return true;
  return true;
}

function normalizeLocationFromApi(raw: Record<string, unknown>): Location {
  return {
    id: Number(raw.id ?? raw.Id),
    name: String(raw.name ?? raw.Name ?? ''),
    address: String(raw.address ?? raw.Address ?? ''),
    timezone: String(raw.timezone ?? raw.Timezone ?? ''),
    latitude: (raw.latitude ?? raw.Latitude) as number | undefined,
    longitude: (raw.longitude ?? raw.Longitude) as number | undefined,
    nightStart: (raw.nightStart ?? raw.NightStart) as string | null | undefined,
    nightEnd: (raw.nightEnd ?? raw.NightEnd) as string | null | undefined,
    isActive: normalizeLocationIsActive(raw.isActive ?? raw.IsActive),
    managerId: (raw.managerId ?? raw.ManagerId) as number | null | undefined,
    manager: (raw.manager ?? raw.Manager) as Employee | null | undefined,
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
    updatedAt: (raw.updatedAt ?? raw.UpdatedAt) as string | undefined,
  };
}

function normalizeLocationsList(data: unknown): Location[] {
  if (!Array.isArray(data)) return [];
  return data.map((item) => normalizeLocationFromApi(item as Record<string, unknown>));
}

// Employee API
export const employeeApi = {
  getAll: async (options?: EmployeeListQueryOptions): Promise<Employee[]> => {
    const key = employeeListCacheKey(options);
    return employeesListCache.get(key, async () => {
      const params: Record<string, unknown> = {
        includePayroll: options?.includePayroll ?? false,
        presignImages: options?.presignImages ?? false,
      };
      if (options?.page != null && options?.pageSize != null) {
        params.page = options.page;
        params.pageSize = options.pageSize;
      }
      const response = await api.get('/employees', { params });
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data?.items && Array.isArray(data.items)) return data.items;
      return data;
    });
  },

  /** Full employee list with payroll + presigned profile images (employee admin / payroll pages). */
  getAllFull: async (): Promise<Employee[]> => {
    return employeeApi.getAll({ includePayroll: true, presignImages: true });
  },

  /** Lean paginated list for the employee admin list page (server-side filters). */
  queryListPage: async (query: EmployeeListPageQuery): Promise<EmployeeListPageResult> => {
    const response = await api.post('/employees/list-page', {
      status: query.status ?? 'Active',
      locationId: query.locationId ?? undefined,
      q: query.q?.trim() || undefined,
      sortColumn: query.sortColumn ?? undefined,
      sortDirection: query.sortDirection ?? 'asc',
      presignImages: query.presignImages ?? true,
      filters: query.filters?.filter((f) => f.field),
    });
    return response.data;
  },

  /** Reference data for advanced filter dropdowns (load when user opens filters). */
  getListPageFilterOptions: async (): Promise<EmployeeListPageFilterOptions> => {
    const response = await api.get('/employees/list-page/filter-options');
    return response.data;
  },

  /** Lightweight list for attendance/scheduling dropdowns (~id, name, location, schedules). */
  getPicker: async (): Promise<EmployeePickerRow[]> => {
    return employeePickerCache.get(async () => {
      const response = await api.get('/employees/picker');
      return response.data;
    });
  },

  getPickerAsEmployees: async (): Promise<Employee[]> => {
    const rows = await employeeApi.getPicker();
    return rows.map(employeePickerToEmployee);
  },
  
  getById: async (id: number): Promise<Employee> => {
    return employeeByIdCache.get(String(id), async () => {
      const response = await api.get(`/employees/${id}`);
      return response.data;
    });
  },
  
  create: async (employee: CreateEmployeeRequest): Promise<Employee> => {
    const response = await api.post('/employees', employee);
    employeesListCache.clear();
    employeePickerCache.invalidate();
    holidayAdminBootstrapCache.invalidate();
    return response.data;
  },
  
  update: async (id: number, employee: UpdateEmployeeRequest): Promise<void> => {
    await api.patch(`/employees/${id}`, employee);
    employeeByIdCache.invalidate(String(id));
    employeesListCache.clear();
    employeePickerCache.invalidate();
    holidayAdminBootstrapCache.invalidate();
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employees/${id}`);
    employeesListCache.clear();
    employeePickerCache.invalidate();
    holidayAdminBootstrapCache.invalidate();
  },

  getLeaveTypes: async (id: number, leaveYear?: number): Promise<EmployeeLeaveTypeInfo[]> => {
    const params = leaveYear ? { leaveYear } : {};
    const response = await api.get(`/employees/${id}/leave-types`, { params });
    return response.data;
  },

  /** Searchable options for line manager (excludes same employee). */
  getLineManagerOptions: async (
    excludeEmployeeId: number,
    q?: string
  ): Promise<{ id: number; firstName: string; lastName: string; label: string }[]> => {
    const response = await api.get('/employees/line-manager-options', {
      params: { excludeEmployeeId, q: q?.trim() || undefined },
    });
    return response.data;
  },

  /** Line-manager chain above this employee and full reporting subtree below. */
  getHierarchy: async (id: number): Promise<EmployeeHierarchyResponse> => {
    const response = await api.get(`/employees/${id}/hierarchy`);
    return response.data;
  },

  /** Line-manager tree; pass locationId to limit to employees at that main or sub-location. */
  addRoleAssignment: async (
    id: number,
    body: { roleId: number; scopeType: string; scopeOrgUnitId?: number | null; scopeLocationId?: number | null }
  ) => {
    const response = await api.post(`/employees/${id}/role-assignments`, body);
    return response.data;
  },

  getRoleAssignments: async (id: number) => {
    const response = await api.get(`/employees/${id}/role-assignments`);
    return response.data;
  },

  getOrganizationHierarchy: async (locationId?: number | null): Promise<OrganizationHierarchyResponse> => {
    const params =
      locationId != null && locationId !== 0 ? { locationId } : undefined;
    const response = await api.get('/employees/organization-hierarchy', { params });
    return response.data;
  },
};

export interface InboxStatusOption {
  key: string;
  label: string;
}

export interface InboxPath {
  name: string;
  steps: InboxStatusOption[];
}

export interface ManagerInboxItem {
  id: string;
  type: string;
  severity: string;
  title: string;
  why: string;
  recommended: string;
  employeeId: number;
  employeeName: string;
  positionTitle?: string | null;
  locationName?: string | null;
  managerSource: 'lineManager' | 'locationManager' | string;
  entityId?: number | null;
  occurredAt?: string | null;
  createdAt?: string | null;
  deepLink?: string | null;
  actions: string[];
  status: string;
  statusLabel: string;
  statusMeaning?: string;
  lane: 'need_action' | 'waiting' | 'muted' | 'watching' | 'done' | string;
  mutedUntil?: string | null;
  outcome?: string | null;
  managerNote?: string | null;
  employeeReply?: string | null;
  statusPaths?: InboxPath[];
  statusPath?: InboxStatusOption[];
  viewerRole?: 'owner' | 'watcher' | 'escalated' | string;
  ownerEmployeeId?: number | null;
  ownerName?: string | null;
  escalatedToEmployeeId?: number | null;
  escalatedToName?: string | null;
  escalationLevel?: number;
  escalationWorkingDays?: number;
  workingDaysPending?: number;
  canTakeOver?: boolean;
  locationId?: number | null;
  hasPhone?: boolean;
  messageId?: number | null;
  unreadCount?: number;
  employeeMessageId?: number | null;
  employeeUnreadCount?: number;
  managerMessageId?: number | null;
  managerUnreadCount?: number;
  history?: ManagerInboxHistoryEntry[];
}

export interface ManagerInboxHistoryEntry {
  at: string;
  action: string;
  label: string;
  actorName?: string | null;
  note?: string | null;
}

export interface ManagerInboxResponse {
  teamCount: number;
  managesTeam: boolean;
  needActionCount: number;
  waitingCount: number;
  watchingCount: number;
  mutedCount: number;
  resolvedTodayCount: number;
  items: ManagerInboxItem[];
}

export interface ManagerInboxTypeSetting {
  type: string;
  label: string;
  escalationWorkingDays: number;
}

export const managerInboxApi = {
  get: async (locationId?: number | null): Promise<ManagerInboxResponse> => {
    const params =
      locationId != null && locationId !== 0 ? { locationId } : undefined;
    const response = await api.get('/manager-inbox', { params });
    return response.data;
  },
  transition: async (body: {
    itemId: string;
    action: string;
    note?: string;
    outcome?: string;
    muteUntil?: string;
  }): Promise<void> => {
    await api.post('/manager-inbox/transition', body);
  },
  getSettings: async (): Promise<ManagerInboxTypeSetting[]> => {
    const response = await api.get('/manager-inbox/settings');
    return response.data;
  },
  saveSettings: async (settings: ManagerInboxTypeSetting[]): Promise<void> => {
    await api.put('/manager-inbox/settings', settings);
  },
  openChat: async (
    itemId: string,
    kind?: 'chat' | 'employee' | 'manager',
  ): Promise<{ messageId: number }> => {
    const response = await api.post('/manager-inbox/chat', { itemId, kind });
    return response.data;
  },
  sendSms: async (itemId: string, message: string): Promise<{ messageId?: number }> => {
    const response = await api.post('/manager-inbox/sms', { itemId, message });
    return response.data;
  },
};

export const approvalsApi = {
  inbox: async (): Promise<ApprovalInboxItem[]> => {
    const response = await api.get('/approvals/inbox');
    return response.data;
  },
  listDelegations: async (): Promise<AuthorityDelegationRow[]> => {
    const response = await api.get('/approvals/delegations');
    return response.data;
  },
  createDelegation: async (body: { toEmployeeId: number; workTypes?: string; effectiveFrom?: string; effectiveTo?: string }) => {
    const response = await api.post('/approvals/delegations', body);
    return response.data;
  },
  revokeDelegation: async (id: number) => {
    await api.put(`/approvals/delegations/${id}/revoke`);
  },
  delegationHistory: async (scope: 'mine' | 'all' = 'mine'): Promise<DelegationHistoryResult> => {
    const response = await api.get('/approvals/delegations/history', { params: { scope } });
    return response.data;
  },
};

// Employee self-service personal details
export const myEmployeeDetailsApi = {
  get: async (): Promise<Partial<Employee>> => {
    const response = await api.get('/my-employee-details');
    return response.data;
  },
  update: async (request: UpdateMyEmployeeDetailsRequest): Promise<Partial<Employee>> => {
    const response = await api.put('/my-employee-details', request);
    return response.data;
  },
};

// Location API
export const locationApi = {
  /** Assigned locations for the signed-in user (header dropdown). */
  getMine: async (): Promise<Location[]> => {
    try {
      return myLocationsListCache.get(async () => {
        const response = await api.get('/locations/mine');
        return normalizeLocationsList(response.data);
      });
    } catch (error: any) {
      console.error('[Location API] getMine error:', error);
      throw error;
    }
  },

  getAll: async (): Promise<Location[]> => {
    try {
      return locationsListCache.get(async () => {
        const response = await api.get('/locations');
        return normalizeLocationsList(response.data);
      });
    } catch (error: any) {
      console.error('[Location API] Error:', error);
      console.error('[Location API] Error response:', error.response);
      console.error('[Location API] Error data:', error.response?.data);
      throw error;
    }
  },

  /** Always hits the network; use after mutations so the settings list cannot show stale cache. */
  fetchAllFresh: async (): Promise<Location[]> => {
    locationsListCache.invalidate();
    const response = await api.get('/locations', {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    });
    const locations = normalizeLocationsList(response.data);
    if (locations.length > 0) {
      locationsListCache.seed(locations);
    }
    return locations;
  },
  
  getById: async (id: number): Promise<Location> => {
    const response = await api.get(`/locations/${id}`);
    return response.data;
  },
  
  create: async (location: CreateLocationRequest): Promise<Location> => {
    const response = await api.post('/locations', location);
    invalidateLocationsReadCaches();
    return response.data;
  },
  
  update: async (id: number, location: CreateLocationRequest, isActive?: boolean): Promise<void> => {
    const locationData = {
      id: id,
      name: location.name,
      address: location.address,
      timezone: location.timezone,
      latitude: location.latitude,
      longitude: location.longitude,
      nightStart: location.nightStart?.trim() ? location.nightStart.trim() : null,
      nightEnd: location.nightEnd?.trim() ? location.nightEnd.trim() : null,
      isActive: isActive !== undefined ? isActive : true,
    };
    await api.put(`/locations/${id}`, locationData);
    invalidateLocationsReadCaches();
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/locations/${id}`);
    invalidateLocationsReadCaches();
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/locations/${id}/deactivate`);
    invalidateLocationsReadCaches();
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/locations/${id}/activate`);
    invalidateLocationsReadCaches();
  },

  setManager: async (locationId: number, managerEmployeeId: number | null): Promise<void> => {
    await api.put(`/locations/${locationId}/manager`, { managerEmployeeId });
    invalidateLocationsReadCaches();
  },
};

// Position API
export const positionApi = {
  getAll: async (): Promise<Position[]> => {
    return positionsListCache.get(async () => {
      const response = await api.get('/positions');
      return response.data;
    });
  },
  
  getById: async (id: number): Promise<Position> => {
    const response = await api.get(`/positions/${id}`);
    return response.data;
  },
  
  create: async (position: CreatePositionRequest): Promise<Position> => {
    const response = await api.post('/positions', position);
    return response.data;
  },
  
  update: async (id: number, position: CreatePositionRequest): Promise<void> => {
    const positionData = {
      id: id,
      title: position.title,
      description: position.description,
      color: position.color,
      isActive: true
    };
    await api.put(`/positions/${id}`, positionData);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/positions/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/positions/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/positions/${id}/activate`);
  },
};

// TimeStation API
export const timeStationApi = {
  getAll: async (): Promise<TimeStation[]> => {
    const response = await api.get('/timestations');
    return response.data;
  },
  
  getById: async (id: number): Promise<TimeStation> => {
    const response = await api.get(`/timestations/${id}`);
    return response.data;
  },
  
  create: async (timeStation: CreateTimeStationRequest): Promise<TimeStation> => {
    const response = await api.post('/timestations', timeStation);
    return response.data;
  },
  
  update: async (id: number, timeStation: CreateTimeStationRequest): Promise<void> => {
    const timeStationData: any = {
      name: timeStation.name,
      deviceId: timeStation.deviceId,
      description: timeStation.description,
      ipAddress: timeStation.ipAddress,
      locationId: timeStation.locationId,
      status: timeStation.status || (timeStation.isActive ? 'Active' : 'Inactive'),
      allowPhotoCapture: timeStation.allowPhotoCapture,
      locationTracking: timeStation.locationTracking,
      allowPinClockIn: timeStation.allowPinClockIn === true
    };
    // Always include allowedWifis (empty array if none)
    timeStationData.allowedWifis = timeStation.allowedWifis || [];
    // Always include allowedIpRanges (empty array if none)
    timeStationData.allowedIpRanges = timeStation.allowedIpRanges || [];
    await api.put(`/timestations/${id}`, timeStationData);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/timestations/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/activate`);
  },
  
  approve: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/approve`);
  },

  reject: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/reject`);
  },
};

// Schedule API
export const scheduleApi = {
  getAll: async (): Promise<Schedule[]> => {
    const response = await api.get('/schedules');
    return response.data;
  },
  
  getById: async (id: number): Promise<Schedule> => {
    const response = await api.get(`/schedules/${id}`);
    return response.data;
  },
  
  create: async (schedule: CreateScheduleRequest): Promise<Schedule> => {
    const response = await api.post('/schedules', schedule);
    return response.data;
  },
  
  update: async (id: number, schedule: CreateScheduleRequest): Promise<Schedule> => {
    const response = await api.put(`/schedules/${id}`, schedule);
    return response.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/schedules/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/schedules/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/schedules/${id}/activate`);
  },
};

export const scheduleExpectedCountApi = {
  getAll: async (params?: { scheduleId?: number }): Promise<ScheduleExpectedCount[]> => {
    const response = await api.get('/schedule-expected-counts', { params });
    return response.data;
  },

  get: async (scheduleId: number): Promise<ScheduleExpectedCount> => {
    const response = await api.get(`/schedule-expected-counts/${scheduleId}`);
    return response.data;
  },

  upsert: async (
    scheduleId: number,
    body: UpsertScheduleExpectedCountRequest
  ): Promise<ScheduleExpectedCount> => {
    const response = await api.put(`/schedule-expected-counts/${scheduleId}`, body);
    return response.data;
  },

  delete: async (scheduleId: number): Promise<void> => {
    await api.delete(`/schedule-expected-counts/${scheduleId}`);
  },

  getWeek: async (
    scheduleId: number,
    params: { locationId: number; weekStart: string }
  ): Promise<ScheduleExpectedCountWeek> => {
    const response = await api.get(`/schedule-expected-counts/${scheduleId}/week`, { params });
    return response.data;
  },

  saveWeek: async (
    scheduleId: number,
    body: UpsertScheduleExpectedCountWeekRequest
  ): Promise<ScheduleExpectedCountWeek> => {
    const response = await api.put(`/schedule-expected-counts/${scheduleId}/week`, body);
    return response.data;
  },
};

export const schedulePlaceholderApi = {
  getAll: async (scheduleId?: number): Promise<SchedulePlaceholder[]> => {
    const response = await api.get('/schedule-placeholders', {
      params: scheduleId ? { scheduleId } : undefined,
    });
    return response.data;
  },
  create: async (body: { scheduleId: number; name: string }): Promise<SchedulePlaceholder> => {
    const response = await api.post('/schedule-placeholders', body);
    return response.data;
  },
  update: async (id: number, body: { scheduleId: number; name: string }): Promise<void> => {
    await api.put(`/schedule-placeholders/${id}`, body);
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/schedule-placeholders/${id}`);
  },
  assign: async (
    id: number,
    body: { employeeId: number; locationId?: number; fromDate?: string; toDate?: string }
  ): Promise<{ assignedShiftCount: number; employeeId: number; placeholderId: number }> => {
    const response = await api.post(`/schedule-placeholders/${id}/assign`, body);
    return response.data;
  },
};

export const scheduleTextReminderApi = {
  get: async (scheduleId: number): Promise<ScheduleTextReminders> => {
    const response = await api.get('/schedule-text-reminders', { params: { scheduleId } });
    return response.data;
  },
  save: async (body: SaveScheduleTextRemindersRequest): Promise<void> => {
    await api.put('/schedule-text-reminders', body);
  },
};

export const myReportsApi = {
  catalog: async (): Promise<{ types: MyReportTypeDef[] }> => {
    const response = await api.get('/my-reports/catalog');
    return response.data;
  },
  listTemplates: async (): Promise<MyReportTemplate[]> => {
    const response = await api.get('/my-reports/templates');
    return response.data;
  },
  getTemplate: async (id: number): Promise<MyReportTemplate> => {
    const response = await api.get(`/my-reports/templates/${id}`);
    return response.data;
  },
  createTemplate: async (body: {
    reportType: string;
    name: string;
    filters: Record<string, string>;
    columns: string[];
  }): Promise<MyReportTemplate> => {
    const response = await api.post('/my-reports/templates', body);
    return response.data;
  },
  updateTemplate: async (
    id: number,
    body: {
      reportType: string;
      name: string;
      filters: Record<string, string>;
      columns: string[];
    }
  ): Promise<MyReportTemplate> => {
    const response = await api.put(`/my-reports/templates/${id}`, body);
    return response.data;
  },
  deleteTemplate: async (id: number): Promise<void> => {
    await api.delete(`/my-reports/templates/${id}`);
  },
  list: async (templateId?: number | null): Promise<MyReportRunListItem[]> => {
    const response = await api.get('/my-reports', {
      params: templateId ? { templateId } : undefined,
    });
    return response.data;
  },
  get: async (id: number): Promise<MyReportRunDetail> => {
    const response = await api.get(`/my-reports/${id}`);
    return response.data;
  },
  create: async (body: {
    reportType: string;
    name?: string;
    templateId?: number | null;
    filters: Record<string, string>;
    columns: string[];
  }): Promise<MyReportRunDetail> => {
    const response = await api.post('/my-reports', body);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/my-reports/${id}`);
  },
};

// Shift Groups API
export const shiftGroupApi = {
  getAll: async (params?: { locationId?: number; includeInactive?: boolean }): Promise<ShiftGroup[]> => {
    const response = await api.get('/shiftgroups', {
      params: {
        ...(params?.locationId ? { locationId: params.locationId } : {}),
        ...(params?.includeInactive ? { includeInactive: true } : {}),
      },
    });
    return response.data;
  },

  getById: async (id: number): Promise<ShiftGroup> => {
    const response = await api.get(`/shiftgroups/${id}`);
    return response.data;
  },

  create: async (group: CreateShiftGroupRequest): Promise<ShiftGroup> => {
    const response = await api.post('/shiftgroups', group);
    return response.data;
  },

  update: async (id: number, group: UpdateShiftGroupRequest): Promise<ShiftGroup> => {
    const response = await api.put(`/shiftgroups/${id}`, group);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/shiftgroups/${id}`);
  },
};

export const payrollDepartmentApi = {
  getAll: async (): Promise<PayrollDepartment[]> => {
    const response = await api.get('/payrolldepartments');
    return response.data;
  },
  
  getById: async (id: number): Promise<PayrollDepartment> => {
    const response = await api.get(`/payrolldepartments/${id}`);
    return response.data;
  },
  
  create: async (department: CreatePayrollDepartmentRequest): Promise<PayrollDepartment> => {
    const response = await api.post('/payrolldepartments', department);
    return response.data;
  },
  
  update: async (id: number, department: CreatePayrollDepartmentRequest): Promise<void> => {
    await api.put(`/payrolldepartments/${id}`, department);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/payrolldepartments/${id}`);
  }
};

export const payrollEmploymentTypesApi = {
  list: async (): Promise<PayrollEmploymentType[]> => {
    const response = await api.get<PayrollEmploymentType[]>('/payroll/employment-types');
    return response.data;
  },
};

// Role API
export const roleApi = {
  getAll: async (): Promise<Role[]> => {
    return rolesListCache.get(async () => {
      const response = await api.get('/roles');
      return Array.isArray(response.data)
        ? response.data
        : Array.isArray((response.data as { $values?: Role[] } | undefined)?.$values)
          ? (response.data as { $values: Role[] }).$values
          : [];
    });
  },

  /** Always hits the network; use after mutations so settings cannot show a poisoned empty cache. */
  fetchAllFresh: async (): Promise<Role[]> => {
    rolesListCache.invalidate();
    const response = await api.get('/roles', {
      params: { _: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    });
    const roles = Array.isArray(response.data)
      ? response.data
      : Array.isArray((response.data as { $values?: Role[] } | undefined)?.$values)
        ? (response.data as { $values: Role[] }).$values
        : [];
    if (roles.length > 0) {
      rolesListCache.seed(roles);
    }
    return roles;
  },
  
  getById: async (id: number): Promise<Role> => {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  },
  
  getPermissions: async (): Promise<Permission[]> => {
    const response = await api.get('/roles/permissions');
    return response.data;
  },
  
  create: async (role: CreateRoleRequest): Promise<Role> => {
    const response = await api.post('/roles', role);
    rolesListCache.invalidate();
    return response.data;
  },
  
  update: async (id: number, role: UpdateRoleRequest): Promise<void> => {
    await api.put(`/roles/${id}`, role);
    rolesListCache.invalidate();
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/roles/${id}`);
    rolesListCache.invalidate();
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/roles/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/roles/${id}/activate`);
  },
};

// Break Time Settings API
export const breakTimeSettingsApi = {
  get: async (): Promise<BreakTimeSettings> => {
    const response = await api.get('/break-time-settings');
    return response.data;
  },
  
  create: async (settings: CreateBreakTimeSettingsRequest): Promise<BreakTimeSettings> => {
    const response = await api.post('/break-time-settings', settings);
    return response.data;
  },
};

// Working time / rest rules (location + position)
export const workingTimeRestRulesApi = {
  list: async (
    locationId?: number,
    options?: { signal?: AbortSignal }
  ): Promise<WorkingTimeRestRule[]> => {
    const response = await api.get('/working-time-rest-rules', {
      params: locationId != null ? { locationId } : undefined,
      timeout: 15000,
      signal: options?.signal,
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  create: async (body: UpsertWorkingTimeRestRuleRequest): Promise<WorkingTimeRestRule> => {
    const response = await api.post('/working-time-rest-rules', body);
    return response.data;
  },

  update: async (id: number, body: UpsertWorkingTimeRestRuleRequest): Promise<void> => {
    await api.put(`/working-time-rest-rules/${id}`, body);
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/working-time-rest-rules/${id}`);
  },

  move: async (id: number, direction: 'up' | 'down'): Promise<void> => {
    await api.post(`/working-time-rest-rules/${id}/move`, { direction });
  },
};

// System Settings API
export const systemSettingsApi = {
  getAll: async (): Promise<SystemSetting[]> => {
    const response = await api.get('/system-settings');
    return response.data;
  },
  
  getByKey: async (key: string): Promise<SystemSetting> => {
    return systemSettingByKeyCache.get(key, async () => {
      const response = await api.get(`/system-settings/${key}`);
      return response.data;
    });
  },
  
  create: async (setting: CreateSystemSettingRequest): Promise<SystemSetting> => {
    const response = await api.post('/system-settings', setting);
    return response.data;
  },
  
  update: async (key: string, value: string): Promise<void> => {
    await api.put(`/system-settings/${key}`, { value });
  },
};

// Training Types API
export const trainingTypeApi = {
  getAll: async (): Promise<TrainingType[]> => {
    const response = await api.get('/trainingtypes');
    return response.data;
  },
  
  getById: async (id: number): Promise<TrainingType> => {
    const response = await api.get(`/trainingtypes/${id}`);
    return response.data;
  },
  
  create: async (trainingType: CreateTrainingTypeRequest): Promise<TrainingType> => {
    const response = await api.post('/trainingtypes', trainingType);
    return response.data;
  },
  
  update: async (id: number, trainingType: UpdateTrainingTypeRequest): Promise<void> => {
    await api.put(`/trainingtypes/${id}`, trainingType);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/trainingtypes/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/trainingtypes/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/trainingtypes/${id}/activate`);
  },
};

// Company Settings API
export const companySettingsApi = {
  get: async (): Promise<CompanySettings> => {
    const settings = await companySettingsCache.get(async () => {
      const response = await api.get('/companysettings');
      const data = response.data as CompanySettings;
      return { ...data, currency: normalizeCurrencyLabel(data.currency) };
    });
    applyCompanyDateFormat(settings.dateFormat);
    return settings;
  },
  
  create: async (settings: CreateCompanySettingsRequest): Promise<CompanySettings> => {
    const response = await api.post('/companysettings', settings);
    companySettingsCache.invalidate();
    const data = response.data as CompanySettings;
    applyCompanyDateFormat(data.dateFormat ?? settings.dateFormat);
    return { ...data, currency: normalizeCurrencyLabel(data.currency) };
  },
  
  update: async (id: number, settings: UpdateCompanySettingsRequest): Promise<void> => {
    await api.put(`/companysettings/${id}`, settings);
    companySettingsCache.invalidate();
    if (settings.dateFormat) applyCompanyDateFormat(settings.dateFormat);
  },

  updateFeatureAccess: async (params: {
    securityCommandControlLogEnabled: boolean;
    mandatoryScheduleInputEnabled: boolean;
    healthcareComplianceEnabled: boolean;
    healthcareNurseBandingPayrollEnabled: boolean;
    mfaEnabled?: boolean;
    mfaAllowAuthenticator?: boolean;
    mfaAllowEmail?: boolean;
    mfaAllowSms?: boolean;
    jwtExpiryValue?: number;
    jwtExpiryUnit?: 'Hours' | 'Days';
  }): Promise<void> => {
    await api.put('/companysettings/feature-access', params);
    companySettingsCache.invalidate();
  },
};

export type NurseBandingSettingsInput = {
  healthcareNurseAlAccrualPercent?: number;
  healthcareNurseBhEligibilityWeeks?: number;
  healthcareNurseBhEligibilityMinHours?: number;
  healthcareNurseBhStandardDayHours?: number;
};

export type NurseBandBaseRateRowInput = {
  id?: number;
  rateCardId?: number;
  displayOrder?: number;
  bandYear?: number | null;
  weekdayHourlyRate: number;
  saturdayHourlyRate: number;
  sundayHourlyRate: number;
  bankHolidayHourlyRate: number;
  midweekRedEyeHourlyRate: number;
  weekendRedEyeHourlyRate: number;
};

export type NurseBandScheduleAdditionalRowInput = {
  id?: number;
  rateCardId?: number;
  displayOrder?: number;
  bandYear?: number | null;
  scheduleId?: number | null;
  scheduleName?: string | null;
  positionId?: number | null;
  positionName?: string | null;
  additionalHourlyRate: number;
};

export type NurseBandPositionRateCardInput = {
  id?: number;
  name: string;
  displayOrder?: number;
  positionIds: number[];
  baseRateRows: NurseBandBaseRateRowInput[];
  scheduleAdditionalRows: NurseBandScheduleAdditionalRowInput[];
};

export type NurseBandYearHistoryRow = {
  id?: number;
  employeeId?: number;
  positionId: number;
  positionName?: string | null;
  effectiveFrom: string;
  bandYear: number;
};

export type NurseBandDayTypeResolvedRate = {
  dayType: string;
  baseRate: number;
  additionalRate: number;
  totalRate: number;
};

export type NurseBandScheduleResolvedRates = {
  scheduleId: number;
  scheduleName: string;
  additionalHourlyRate: number;
  hasRateCard1Match: boolean;
  dayTypes: NurseBandDayTypeResolvedRate[];
};

export type NurseBandPositionResolvedRates = {
  positionId: number;
  positionName: string;
  rateCardId?: number | null;
  rateCardName?: string | null;
  bandYear: number | null;
  baseRates: NurseBandDayTypeResolvedRate[];
  schedules: NurseBandScheduleResolvedRates[];
};

export type NurseBandResolvedRates = {
  employeeId: number;
  asOf: string;
  bandYear: number | null;
  rateCardId?: number | null;
  baseRates: NurseBandDayTypeResolvedRate[];
  schedules: NurseBandScheduleResolvedRates[];
  positions?: NurseBandPositionResolvedRates[];
};

export const nurseBandingApi = {
  getRateCards: async (): Promise<{
    settings: NurseBandingSettingsInput | null;
    rateCards: Array<{ id: number; name: string; displayOrder: number; positionIds: number[] }>;
    baseRateRows: NurseBandBaseRateRowInput[];
    scheduleAdditionalRows: NurseBandScheduleAdditionalRowInput[];
  }> => {
    const response = await api.get('/nurse-banding/rate-cards');
    return response.data;
  },

  saveRateCards: async (payload: {
    settings?: NurseBandingSettingsInput;
    rateCards: NurseBandPositionRateCardInput[];
  }): Promise<void> => {
    await api.put('/nurse-banding/rate-cards', payload);
  },

  getBandYearHistory: async (employeeId: number): Promise<NurseBandYearHistoryRow[]> => {
    const response = await api.get(`/nurse-banding/employees/${employeeId}/band-year-history`);
    return response.data;
  },

  saveBandYearHistory: async (employeeId: number, rows: NurseBandYearHistoryRow[]): Promise<void> => {
    await api.put(`/nurse-banding/employees/${employeeId}/band-year-history`, rows);
  },

  getReferenceWeekdayRate: async (
    employeeId: number,
    asOf?: string
  ): Promise<{ weekdayHourlyRate: number | null; bandYear: number | null; asOf: string }> => {
    const response = await api.get(`/nurse-banding/employees/${employeeId}/reference-weekday-rate`, {
      params: asOf ? { asOf } : undefined,
    });
    return response.data;
  },

  getResolvedRates: async (
    employeeId: number,
    options?: { asOf?: string; extraScheduleIds?: number[] }
  ): Promise<NurseBandResolvedRates> => {
    const response = await api.get(`/nurse-banding/employees/${employeeId}/resolved-rates`, {
      params: {
        ...(options?.asOf ? { asOf: options.asOf } : {}),
        ...(options?.extraScheduleIds?.length
          ? { extraScheduleIds: options.extraScheduleIds.join(',') }
          : {}),
      },
    });
    return response.data;
  },
};

export type SecurityCommandControlLogAttachment = {
  id: number;
  fileName: string;
  filePath: string;
  contentType?: string | null;
  fileSize: number;
  createdAt: string;
};

export type SecurityCommandControlLogIncident = {
  id: number;
  entryId: number;
  incidentNo: string;
  incidentTime: string;
  location: string;
  details: string;
  actionBy: string;
  outcome: string;
  photosCctvDescription?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  attachments: SecurityCommandControlLogAttachment[];
};

export type SecurityCommandControlLogEntry = {
  id: number;
  logDate: string;
  event: string;
  location: string;
  loggerEmployeeId: number;
  loggerName?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  incidents: SecurityCommandControlLogIncident[];
};

export const securityCommandControlLogApi = {
  getAll: async (): Promise<SecurityCommandControlLogEntry[]> => {
    const response = await api.get('/security-command-control-log');
    return response.data;
  },
  getById: async (id: number): Promise<SecurityCommandControlLogEntry> => {
    const response = await api.get(`/security-command-control-log/${id}`);
    return response.data;
  },
  createEntry: async (body: { logDate: string; event: string; location: string }): Promise<SecurityCommandControlLogEntry> => {
    const response = await api.post('/security-command-control-log', body);
    return response.data;
  },
  updateEntry: async (id: number, body: { logDate?: string; event?: string; location?: string }): Promise<void> => {
    await api.put(`/security-command-control-log/${id}`, body);
  },
  deleteEntry: async (id: number): Promise<void> => {
    await api.delete(`/security-command-control-log/${id}`);
  },
  createIncident: async (
    entryId: number,
    body: {
      incidentNo: string;
      incidentTime?: string;
      location?: string;
      details?: string;
      actionBy?: string;
      outcome?: string;
      photosCctvDescription?: string;
    }
  ): Promise<SecurityCommandControlLogIncident> => {
    const response = await api.post(`/security-command-control-log/${entryId}/incidents`, body);
    return response.data;
  },
  updateIncident: async (
    entryId: number,
    incidentId: number,
    body: Partial<{
      incidentNo: string;
      incidentTime: string;
      location: string;
      details: string;
      actionBy: string;
      outcome: string;
      photosCctvDescription: string;
    }>
  ): Promise<void> => {
    await api.put(`/security-command-control-log/${entryId}/incidents/${incidentId}`, body);
  },
  deleteIncident: async (entryId: number, incidentId: number): Promise<void> => {
    await api.delete(`/security-command-control-log/${entryId}/incidents/${incidentId}`);
  },
  uploadAttachments: async (entryId: number, incidentId: number, files: File[]): Promise<{ attachments: SecurityCommandControlLogAttachment[] }> => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const response = await api.post(
      `/security-command-control-log/${entryId}/incidents/${incidentId}/attachments`,
      fd
    );
    return response.data;
  },
  deleteAttachment: async (entryId: number, incidentId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/security-command-control-log/${entryId}/incidents/${incidentId}/attachments/${attachmentId}`);
  },

  bulkDeleteIncidents: async (entryId: number, incidentIds: number[]): Promise<{ deletedCount: number }> => {
    const response = await api.post(`/security-command-control-log/${entryId}/incidents/bulk-delete`, { incidentIds });
    return response.data;
  },

  importValidate: async (
    entryId: number,
    payload: {
      fileName?: string;
      mapping: ScclImportColumnMapping;
      rows: ScclImportRowInput[];
    },
    sourceFile?: File | null
  ): Promise<ScclImportValidateResponse> => {
    if (sourceFile) {
      const fd = new FormData();
      fd.append('payload', JSON.stringify({ ...payload, fileName: payload.fileName ?? sourceFile.name }));
      fd.append('file', sourceFile, sourceFile.name);
      const response = await api.post(`/security-command-control-log/${entryId}/import/validate`, fd);
      return response.data;
    }
    const response = await api.post(`/security-command-control-log/${entryId}/import/validate`, payload);
    return response.data;
  },

  importCommit: async (entryId: number, batchId: number): Promise<ScclImportCommitResponse> => {
    const response = await api.post(`/security-command-control-log/${entryId}/import/commit`, { batchId });
    return response.data;
  },

  importHistory: async (
    entryId: number,
    options?: { page?: number; pageSize?: number; fromDate?: string; toDate?: string }
  ): Promise<ScclImportHistoryPage> => {
    const params: Record<string, string | number> = {
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 20,
    };
    if (options?.fromDate) params.fromDate = options.fromDate;
    if (options?.toDate) params.toDate = options.toDate;
    const response = await api.get(`/security-command-control-log/${entryId}/import/history`, { params });
    return response.data;
  },

  importBatchDetail: async (batchId: number): Promise<ScclImportBatchDetail> => {
    const response = await api.get(`/security-command-control-log/import/batches/${batchId}`);
    return response.data;
  },

  importDownloadUploadedFile: async (batchId: number): Promise<Blob> => {
    const response = await api.get(`/security-command-control-log/import/batches/${batchId}/file`, { responseType: 'blob' });
    return response.data as Blob;
  },
};

// Admin Uploads API (Super Admin only)
export const adminUploadsApi = {
  getContext: async (): Promise<{
    environment: string;
    tenantId: number;
    awsRegion?: string | null;
    uploadsBucket?: string | null;
    serverTimeUtc: string;
  }> => {
    const response = await api.get('/admin/uploads/context');
    return response.data;
  },
  migrateProfileImages: async (body: {
    dryRun?: boolean;
    deleteLocalAfterUpload?: boolean;
    forceReupload?: boolean;
    presignMinutes?: number;
  }): Promise<any> => {
    const response = await api.post('/admin/uploads/migrate-profile-images', body);
    return response.data;
  },
  migrateAll: async (body: {
    dryRun?: boolean;
    deleteLocalAfterUpload?: boolean;
    forceReupload?: boolean;
    presignMinutes?: number;
    includeProfileImages?: boolean;
    includeTaskAttachments?: boolean;
    includeTaskConversationAttachments?: boolean;
    includeSubTaskAttachments?: boolean;
    includeSubTaskConversationAttachments?: boolean;
    includeSecurityCommandControlLogAttachments?: boolean;
  }): Promise<any> => {
    const response = await api.post('/admin/uploads/migrate-all', body);
    return response.data;
  },
};

// Public Holidays API
export const publicHolidayApi = {
  getAll: async (year?: number): Promise<PublicHoliday[]> => {
    const params = year ? { year } : {};
    const response = await api.get('/publicholidays', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<PublicHoliday> => {
    const response = await api.get(`/publicholidays/${id}`);
    return response.data;
  },
  
  create: async (holiday: CreatePublicHolidayRequest): Promise<PublicHoliday> => {
    const response = await api.post('/publicholidays', holiday);
    return response.data;
  },
  
  update: async (id: number, holiday: UpdatePublicHolidayRequest): Promise<void> => {
    await api.put(`/publicholidays/${id}`, holiday);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/publicholidays/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/publicholidays/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/publicholidays/${id}/activate`);
  },
};

// Public Holiday Policy API
export const publicHolidayPolicyApi = {
  getAll: async (): Promise<PublicHolidayPolicy[]> => {
    const response = await api.get('/publicholidaypolicies');
    return response.data;
  },
  
  getById: async (id: number): Promise<PublicHolidayPolicy> => {
    const response = await api.get(`/publicholidaypolicies/${id}`);
    return response.data;
  },
  
  getActive: async (): Promise<PublicHolidayPolicy | null> => {
    const response = await api.get('/publicholidaypolicies/active');
    return response.data;
  },
  
  create: async (policy: CreatePublicHolidayPolicyRequest): Promise<PublicHolidayPolicy> => {
    const response = await api.post('/publicholidaypolicies', policy);
    return response.data;
  },
  
  update: async (id: number, policy: UpdatePublicHolidayPolicyRequest): Promise<void> => {
    await api.put(`/publicholidaypolicies/${id}`, policy);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/publicholidaypolicies/${id}`);
  },
};

// Employee License API
export const employeeLicenseApi = {
  getByEmployeeId: async (employeeId: number): Promise<EmployeeLicense[]> => {
    const response = await api.get(`/employee-licenses?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeLicense> => {
    const response = await api.get(`/employee-licenses/${id}`);
    return response.data;
  },
  
  create: async (license: CreateEmployeeLicenseRequest): Promise<EmployeeLicense> => {
    const response = await api.post('/employee-licenses', license);
    return response.data;
  },
  
  update: async (id: number, license: UpdateEmployeeLicenseRequest): Promise<void> => {
    await api.put(`/employee-licenses/${id}`, license);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-licenses/${id}`);
  },

  uploadCertificate: async (id: number, file: File): Promise<EmployeeLicense> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/employee-licenses/${id}/certificate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getCertificateUrl: async (id: number): Promise<{ url: string; fileName: string; uploadedAt?: string; contentType?: string }> => {
    const response = await api.get(`/employee-licenses/${id}/certificate-url`);
    return response.data;
  },

  downloadCertificate: async (id: number, disposition: 'inline' | 'attachment' = 'attachment'): Promise<Blob> => {
    const response = await api.get(`/employee-licenses/${id}/certificate`, {
      params: { disposition },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Payment Type API
export const paymentTypeApi = {
  getAll: async (): Promise<PaymentType[]> => {
    const response = await api.get('/payment-types');
    return response.data;
  },
  
  getById: async (id: number): Promise<PaymentType> => {
    const response = await api.get(`/payment-types/${id}`);
    return response.data;
  },
};

// Payment Frequency API
export const paymentFrequencyApi = {
  getAll: async (): Promise<PaymentFrequency[]> => {
    const response = await api.get('/payment-frequencies');
    return response.data;
  },
  
  getById: async (id: number): Promise<PaymentFrequency> => {
    const response = await api.get(`/payment-frequencies/${id}`);
    return response.data;
  },
};

// Assign Labor Cost By Option API
export const assignLaborCostByOptionApi = {
  getAll: async (): Promise<AssignLaborCostByOption[]> => {
    const response = await api.get('/assign-labor-cost-by-options');
    return response.data;
  },
  
  getById: async (id: number): Promise<AssignLaborCostByOption> => {
    const response = await api.get(`/assign-labor-cost-by-options/${id}`);
    return response.data;
  },
};

// Working Day API
export const workingDayApi = {
  getAll: async (): Promise<WorkingDay[]> => {
    const response = await api.get('/working-days');
    return response.data;
  },
  
  getById: async (id: number): Promise<WorkingDay> => {
    const response = await api.get(`/working-days/${id}`);
    return response.data;
  },
};

// Pay Record API
export const payRecordApi = {
  getAll: async (): Promise<PayRecord[]> => {
    const response = await api.get('/pay-records');
    return response.data;
  },
  
  getByEmployeeId: async (employeeId: number): Promise<PayRecord[]> => {
    const response = await api.get(`/pay-records?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<PayRecord> => {
    const response = await api.get(`/pay-records/${id}`);
    return response.data;
  },
  
  create: async (payRecord: CreatePayRecordRequest): Promise<PayRecord> => {
    const response = await api.post('/pay-records', payRecord);
    return response.data;
  },
  
  update: async (id: number, payRecord: UpdatePayRecordRequest): Promise<void> => {
    await api.put(`/pay-records/${id}`, payRecord);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/pay-records/${id}`);
  },
};

// Feedback Record API
export const feedbackRecordApi = {
  getByEmployeeId: async (employeeId: number): Promise<FeedbackRecord[]> => {
    const response = await api.get(`/feedback-records?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<FeedbackRecord> => {
    const response = await api.get(`/feedback-records/${id}`);
    return response.data;
  },
  
  create: async (feedbackRecord: CreateFeedbackRecordRequest): Promise<FeedbackRecord> => {
    const response = await api.post('/feedback-records', feedbackRecord);
    return response.data;
  },
  
  update: async (id: number, feedbackRecord: UpdateFeedbackRecordRequest): Promise<void> => {
    await api.put(`/feedback-records/${id}`, feedbackRecord);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/feedback-records/${id}`);
  },
};

// Employee Note API
export const employeeNoteApi = {
  getByEmployeeId: async (employeeId: number): Promise<EmployeeNote[]> => {
    const response = await api.get(`/employee-notes?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeNote> => {
    const response = await api.get(`/employee-notes/${id}`);
    return response.data;
  },
  
  create: async (employeeNote: CreateEmployeeNoteRequest): Promise<EmployeeNote> => {
    const response = await api.post('/employee-notes', employeeNote);
    return response.data;
  },
  
  update: async (id: number, employeeNote: UpdateEmployeeNoteRequest): Promise<void> => {
    await api.put(`/employee-notes/${id}`, employeeNote);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-notes/${id}`);
  },
};

// Training Record API
export const trainingRecordApi = {
  getByEmployeeId: async (employeeId: number): Promise<TrainingRecord[]> => {
    const response = await api.get(`/training-records?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<TrainingRecord> => {
    const response = await api.get(`/training-records/${id}`);
    return response.data;
  },
  
  create: async (trainingRecord: CreateTrainingRecordRequest): Promise<TrainingRecord> => {
    const response = await api.post('/training-records', trainingRecord);
    return response.data;
  },
  
  update: async (id: number, trainingRecord: UpdateTrainingRecordRequest): Promise<void> => {
    await api.put(`/training-records/${id}`, trainingRecord);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/training-records/${id}`);
  },
};

// Training Record Cycles API (for recurring trainings)
export const trainingRecordCycleApi = {
  getByTrainingRecordId: async (trainingRecordId: number): Promise<TrainingRecordCycle[]> => {
    const response = await api.get(`/training-records/${trainingRecordId}/cycles`);
    return response.data;
  },
  
  getById: async (trainingRecordId: number, id: number): Promise<TrainingRecordCycle> => {
    const response = await api.get(`/training-records/${trainingRecordId}/cycles/${id}`);
    return response.data;
  },
  
  create: async (trainingRecordId: number, cycle: CreateTrainingRecordCycleRequest): Promise<TrainingRecordCycle> => {
    const response = await api.post(`/training-records/${trainingRecordId}/cycles`, cycle);
    return response.data;
  },
  
  update: async (trainingRecordId: number, id: number, cycle: UpdateTrainingRecordCycleRequest): Promise<void> => {
    await api.put(`/training-records/${trainingRecordId}/cycles/${id}`, cycle);
  },
  
  delete: async (trainingRecordId: number, id: number): Promise<void> => {
    await api.delete(`/training-records/${trainingRecordId}/cycles/${id}`);
  },
};

// Holiday Admin bootstrap (single round-trip for admin page)
export const holidayAdminApi = {
  getBootstrap: async (bustCache = false): Promise<HolidayAdminBootstrap> => {
    if (bustCache) {
      holidayAdminBootstrapCache.invalidate();
    }
    return holidayAdminBootstrapCache.get(async () => {
      const response = await api.get('/holiday-admin/bootstrap');
      return response.data;
    });
  },
};

// Holiday Request API
export const holidayRequestApi = {
  getAll: async (employeeId?: number): Promise<HolidayRequest[]> => {
    const cacheKey = employeeId != null ? `employee:${employeeId}` : 'all';
    return holidayRequestsListCache.get(cacheKey, async () => {
      const url = employeeId ? `/holiday-requests?employeeId=${employeeId}` : '/holiday-requests';
      const response = await api.get(url);
      return response.data;
    });
  },
  
  getById: async (id: number): Promise<HolidayRequest> => {
    const response = await api.get(`/holiday-requests/${id}`);
    return response.data;
  },
  
  create: async (holidayRequest: CreateHolidayRequestRequest): Promise<HolidayRequest> => {
    const response = await api.post('/holiday-requests', holidayRequest);
    holidayRequestsListCache.clear();
    holidayAdminBootstrapCache.invalidate();
    return response.data;
  },
  
  /** Pending/approved leave counts by schedule for schedules the current user is linked to. */
  getMyScheduleCoverage: async (
    startDate: string,
    endDate: string
  ): Promise<{
    schedules: { id: number; name: string }[];
    days: {
      date: string;
      schedules: {
        scheduleId: number;
        scheduleName: string;
        count: number;
        pending: number;
        approved: number;
      }[];
    }[];
  }> => {
    const response = await api.get('/holiday-requests/my-schedule-coverage', {
      params: { startDate, endDate },
    });
    return response.data;
  },

  update: async (id: number, holidayRequest: UpdateHolidayRequestRequest): Promise<void> => {
    await api.put(`/holiday-requests/${id}`, holidayRequest);
    holidayRequestsListCache.clear();
    holidayAdminBootstrapCache.invalidate();
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/holiday-requests/${id}`);
    holidayRequestsListCache.clear();
    holidayAdminBootstrapCache.invalidate();
  },

  cancel: async (holidayRequest: HolidayRequest): Promise<void> => {
    await api.put(`/holiday-requests/${holidayRequest.id}`, {
      startDate: holidayRequest.startDate,
      endDate: holidayRequest.endDate,
      managerSupervisor: holidayRequest.managerSupervisor,
      venueDepartment: holidayRequest.venueDepartment,
      reason: holidayRequest.reason,
      status: 'Cancelled',
    });
    holidayRequestsListCache.clear();
    holidayAdminBootstrapCache.invalidate();
  },
  
  approve: async (id: number): Promise<void> => {
    await api.put(`/holiday-requests/${id}/approve`);
    holidayRequestsListCache.clear();
    holidayAdminBootstrapCache.invalidate();
  },
  
  reject: async (id: number, rejectionReason?: string): Promise<void> => {
    await api.put(`/holiday-requests/${id}/reject`, { rejectionReason });
    holidayRequestsListCache.clear();
    holidayAdminBootstrapCache.invalidate();
  },
};

export const unavailabilityRequestApi = {
  getAll: async (employeeId?: number, status?: string): Promise<UnavailabilityRequest[]> => {
    const params: Record<string, string | number> = {};
    if (employeeId) params.employeeId = employeeId;
    if (status) params.status = status;
    const response = await api.get('/unavailability-requests', { params });
    return response.data;
  },
  getById: async (id: number): Promise<UnavailabilityRequest> => {
    const response = await api.get(`/unavailability-requests/${id}`);
    return response.data;
  },
  checkClashes: async (request: UpsertUnavailabilityRequest): Promise<UnavailabilityClashShift[]> => {
    const response = await api.post('/unavailability-requests/clashes', request);
    return response.data?.clashes ?? [];
  },
  create: async (request: UpsertUnavailabilityRequest): Promise<UnavailabilityRequest> => {
    const response = await api.post('/unavailability-requests', request);
    return response.data;
  },
  update: async (id: number, request: UpsertUnavailabilityRequest): Promise<void> => {
    await api.put(`/unavailability-requests/${id}`, request);
  },
  cancel: async (id: number): Promise<void> => {
    await api.put(`/unavailability-requests/${id}/cancel`);
  },
  skipDate: async (id: number, date: string, reason?: string): Promise<void> => {
    await api.post(`/unavailability-requests/${id}/skip-dates`, { date, reason });
  },
  approve: async (id: number): Promise<void> => {
    await api.put(`/unavailability-requests/${id}/approve`);
  },
  reject: async (id: number, rejectionReason?: string): Promise<void> => {
    await api.put(`/unavailability-requests/${id}/reject`, { rejectionReason });
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/unavailability-requests/${id}`);
  },
};

// Blocked Dates API
export const blockedDateApi = {
  getAll: async (): Promise<BlockedDate[]> => {
    const response = await api.get('/blocked-dates');
    return response.data;
  },

  create: async (blockedDate: CreateBlockedDateRequest): Promise<BlockedDate> => {
    const response = await api.post('/blocked-dates', blockedDate);
    holidayAdminBootstrapCache.invalidate();
    return response.data;
  },

  update: async (id: number, blockedDate: UpdateBlockedDateRequest): Promise<void> => {
    await api.put(`/blocked-dates/${id}`, blockedDate);
    holidayAdminBootstrapCache.invalidate();
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/blocked-dates/${id}`);
    holidayAdminBootstrapCache.invalidate();
  },
};

// Incidents API
export const incidentApi = {
  getAll: async (params?: { employeeId?: number; locationId?: number }): Promise<Incident[]> => {
    const queryParams: Record<string, number> = {};
    if (params?.employeeId) queryParams.employeeId = params.employeeId;
    if (params?.locationId) queryParams.locationId = params.locationId;
    const response = await api.get('/incidents', { params: queryParams });
    return response.data;
  },

  getById: async (id: number): Promise<Incident> => {
    const response = await api.get(`/incidents/${id}`);
    return response.data;
  },

  create: async (incident: CreateIncidentRequest): Promise<Incident> => {
    const response = await api.post('/incidents', incident);
    return response.data;
  },

  update: async (id: number, incident: UpdateIncidentRequest): Promise<void> => {
    await api.put(`/incidents/${id}`, incident);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/incidents/${id}`);
  },
};

export const jobApplicantApi = {
  getAll: async (params?: {
    search?: string;
    applyingFor?: string;
    sourceOfApplication?: string;
    applicationStatus?: string;
    initialScreenPassed?: string;
    invitedForInterview?: string;
    dateAppliedFrom?: string;
    dateAppliedTo?: string;
  }): Promise<JobApplicant[]> => {
    const response = await api.get('/job-applicants', { params });
    return response.data;
  },

  getById: async (id: number): Promise<JobApplicant> => {
    const response = await api.get(`/job-applicants/${id}`);
    return response.data;
  },

  create: async (payload: JobApplicantUpsertRequest): Promise<JobApplicant> => {
    const response = await api.post('/job-applicants', payload);
    return response.data;
  },

  update: async (id: number, payload: JobApplicantUpsertRequest): Promise<void> => {
    await api.put(`/job-applicants/${id}`, payload);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/job-applicants/${id}`);
  },

  bulkCreate: async (applicants: JobApplicantUpsertRequest[]): Promise<JobApplicantBulkCreateResponse> => {
    const response = await api.post('/job-applicants/bulk', { applicants });
    return response.data;
  },

  importValidate: async (payload: JobApplicantImportValidateRequest): Promise<JobApplicantImportValidateResponse> => {
    const response = await api.post('/job-applicants/import/validate', payload);
    return response.data;
  },

  importCommit: async (applicants: JobApplicantUpsertRequest[]): Promise<JobApplicantImportCommitResponse> => {
    const response = await api.post('/job-applicants/import/commit', { applicants });
    return response.data;
  },
};

export const jobApplicantListValueApi = {
  getAll: async (category?: string): Promise<JobApplicantListValue[]> => {
    const response = await api.get('/job-applicant-list-values', { params: category ? { category } : undefined });
    return response.data;
  },

  create: async (payload: { category: string; value: string; displayOrder?: number }): Promise<JobApplicantListValue> => {
    const response = await api.post('/job-applicant-list-values', payload);
    return response.data;
  },

  update: async (
    id: number,
    payload: { value: string; displayOrder?: number; category?: string }
  ): Promise<JobApplicantListValue> => {
    const response = await api.put(`/job-applicant-list-values/${id}`, payload);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/job-applicant-list-values/${id}`);
  },

  getUsageCount: async (id: number): Promise<{ count: number; inUse: boolean }> => {
    const response = await api.get(`/job-applicant-list-values/${id}/usage-count`);
    return response.data;
  },
};

// Shifts API
export const shiftApi = {
  getAll: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    scheduleId?: number;
    employeeId?: number;
    publishedOnly?: boolean;
    bypassCache?: boolean;
  }): Promise<Shift[]> => {
    const { bypassCache, ...query } = params ?? {};
    const key = paramsCacheKey('shifts', query as Record<string, unknown>);
    if (bypassCache) {
      shiftsListCache.invalidate(key);
    }
    return shiftsListCache.get(key, async () => {
      const response = await api.get('/shifts', { params: query });
      return response.data;
    });
  },

  getById: async (id: number): Promise<Shift> => {
    const response = await api.get(`/shifts/${id}`);
    return response.data;
  },

  create: async (shift: CreateShiftRequest): Promise<Shift & { restRuleWarnings?: string[] }> => {
    const response = await api.post('/shifts', shift);
    shiftsListCache.clear();
    return response.data;
  },

  update: async (
    id: number,
    shift: UpdateShiftRequest
  ): Promise<{ restRuleWarnings?: string[] }> => {
    const response = await api.patch(`/shifts/${id}`, shift);
    shiftsListCache.clear();
    return response.data ?? {};
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/shifts/${id}`);
    shiftsListCache.clear();
  },

  deleteWeek: async (weekStart: string): Promise<{ deletedCount: number }> => {
    const response = await api.delete(`/shifts/week/${weekStart}`);
    shiftsListCache.clear();
    return response.data;
  },

  getCounts: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
  }): Promise<Array<{ scheduleId: number; totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }>> => {
    const response = await api.get('/shifts/counts', { params });
    return response.data;
  },

  getColleagues: async (params: {
    employeeId: number;
    startDate: string;
    endDate: string;
  }): Promise<ShiftColleaguesResponse> => {
    const response = await api.get('/shifts/colleagues', { params });
    return response.data;
  },
};

export const shiftMarketplaceApi = {
  getRequests: async (params?: {
    locationId?: number;
    status?: string;
    mine?: boolean;
    approvals?: boolean;
  }): Promise<ShiftMarketplaceRequest[]> => {
    const response = await api.get('/shift-marketplace/requests', { params });
    return response.data;
  },

  create: async (body: CreateMarketplaceRequestPayload): Promise<ShiftMarketplaceRequest> => {
    const response = await api.post('/shift-marketplace/requests', body);
    return response.data;
  },

  accept: async (id: number, notes?: string): Promise<ShiftMarketplaceRequest> => {
    const response = await api.post(`/shift-marketplace/requests/${id}/accept`, { notes });
    return response.data;
  },

  decline: async (id: number, notes?: string): Promise<ShiftMarketplaceRequest> => {
    const response = await api.post(`/shift-marketplace/requests/${id}/decline`, { notes, reason: notes });
    return response.data;
  },

  cancel: async (id: number): Promise<ShiftMarketplaceRequest> => {
    const response = await api.post(`/shift-marketplace/requests/${id}/cancel`);
    return response.data;
  },

  approve: async (id: number, notes?: string): Promise<ShiftMarketplaceRequest> => {
    const response = await api.post(`/shift-marketplace/requests/${id}/approve`, { notes });
    return response.data;
  },

  reject: async (id: number, notes?: string): Promise<ShiftMarketplaceRequest> => {
    const response = await api.post(`/shift-marketplace/requests/${id}/reject`, { notes, reason: notes });
    return response.data;
  },
};

export const schedulingApi = {
  getBootstrap: async (signal?: AbortSignal): Promise<SchedulingBootstrapResponse> => {
    const response = await api.get('/scheduling/bootstrap', { signal });
    return response.data;
  },

  getWeekView: async (
    params: {
      locationId: number;
      scheduleId: number;
      weekStart: string;
      weekEnd: string;
    },
    signal?: AbortSignal
  ): Promise<SchedulingWeekViewResponse> => {
    const response = await api.get('/scheduling/week-view', { params, signal });
    return response.data;
  },

  getPublicationStatuses: async (
    locationId: number,
    weekStart: string,
    signal?: AbortSignal
  ): Promise<Record<number, SchedulingPublicationStatus>> => {
    const response = await api.get('/scheduling/publication-statuses', {
      params: { locationId, weekStart },
      signal,
    });
    const raw = response.data as Record<string, SchedulingPublicationStatus>;
    const out: Record<number, SchedulingPublicationStatus> = {};
    for (const [key, value] of Object.entries(raw || {})) {
      out[Number(key)] = value;
    }
    return out;
  },

  importWeek: async (payload: {
    locationId: number;
    scheduleId: number;
    sourceWeekStartDate: string;
    targetWeekStartDate: string;
  }): Promise<{ deletedCount: number; importedCount: number; skippedHolidayCount: number }> => {
    const response = await api.post('/scheduling/import-week', payload);
    return response.data;
  },
};

/** Schedule shift Excel import (validate → commit) + history + bulk delete */
export const scheduleShiftImportApi = {
  importValidate: async (
    payload: {
      locationId: number;
      scheduleId: number;
      fileName?: string;
      mapping: ScheduleShiftImportColumnMapping;
      rows: ScheduleShiftImportRowInput[];
      allowedWeekStartDate: string;
      allowedWeekEndDate: string;
    },
    sourceFile?: File | null
  ): Promise<ScheduleShiftImportValidateResponse> => {
    if (sourceFile) {
      const fd = new FormData();
      fd.append(
        'payload',
        JSON.stringify({
          locationId: payload.locationId,
          scheduleId: payload.scheduleId,
          fileName: payload.fileName ?? sourceFile.name,
          mapping: payload.mapping,
          rows: payload.rows,
          allowedWeekStartDate: payload.allowedWeekStartDate,
          allowedWeekEndDate: payload.allowedWeekEndDate,
        })
      );
      fd.append('file', sourceFile, sourceFile.name);
      const response = await api.post('/schedule-shift-import/validate', fd);
      return response.data;
    }
    const response = await api.post('/schedule-shift-import/validate', payload);
    return response.data;
  },

  importDownloadUploadedFile: async (batchId: number): Promise<Blob> => {
    const response = await api.get(`/schedule-shift-import/${batchId}/file`, { responseType: 'blob' });
    return response.data as Blob;
  },

  importCommit: async (
    batchId: number,
    overlapResolution: 'Skip' | 'Overwrite'
  ): Promise<ScheduleShiftImportCommitResponse> => {
    const response = await api.post('/schedule-shift-import/commit', { batchId, overlapResolution });
    return response.data;
  },

  importBulkDelete: async (payload: {
    locationId: number;
    scheduleId: number;
    weekStartDate: string;
    shiftIds: number[];
  }): Promise<ScheduleShiftImportBulkDeleteResponse> => {
    const response = await api.post('/schedule-shift-import/bulk-delete', payload);
    return response.data;
  },

  importHistory: async (
    locationId: number,
    scheduleId: number,
    options?: {
      page?: number;
      pageSize?: number;
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<ScheduleShiftImportHistoryPage> => {
    const params: Record<string, string | number> = {
      locationId,
      scheduleId,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 20,
    };
    if (options?.fromDate) params.fromDate = options.fromDate;
    if (options?.toDate) params.toDate = options.toDate;
    const response = await api.get('/schedule-shift-import/history', { params });
    return response.data;
  },

  importBatchDetail: async (batchId: number): Promise<ScheduleShiftImportBatchDetail> => {
    const response = await api.get(`/schedule-shift-import/${batchId}`);
    return response.data;
  },
};

// Shift Templates API
export const shiftTemplateApi = {
  getAll: async (locationId?: number, includeInactive = false): Promise<ShiftTemplate[]> => {
    const params: Record<string, number | boolean> = {};
    if (locationId) params.locationId = locationId;
    if (includeInactive) params.includeInactive = true;
    const response = await api.get('/shift-templates', { params });
    return response.data;
  },

  getById: async (id: number): Promise<ShiftTemplate> => {
    const response = await api.get(`/shift-templates/${id}`);
    return response.data;
  },

  create: async (template: CreateShiftTemplateRequest): Promise<ShiftTemplate> => {
    const response = await api.post('/shift-templates', template);
    return response.data;
  },

  update: async (id: number, template: UpdateShiftTemplateRequest): Promise<void> => {
    await api.put(`/shift-templates/${id}`, template);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/shift-templates/${id}`);
  },
};

// Employee Schedule Sort Order API
export const employeeSortOrderApi = {
  get: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ employeeId: number; sortOrder: number }[]> => {
    const response = await api.get('/employee-schedule-sort-order', {
      params: { locationId, scheduleId, weekStartDate }
    });
    return response.data;
  },
  
  save: async (locationId: number, scheduleId: number, weekStartDate: string, sortOrders: { employeeId: number; sortOrder: number }[]): Promise<void> => {
    await api.post('/employee-schedule-sort-order', {
      locationId,
      scheduleId,
      weekStartDate,
      sortOrders
    });
  },
};

export const schedulePublicationStatusApi = {
  get: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string; id?: number; publishedAt?: string; changedAt?: string }> => {
    const response = await api.get('/schedule-publication-status', {
      params: { locationId, scheduleId, weekStartDate }
    });
    return response.data;
  },
  publish: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string; id: number; publishedAt: string }> => {
    const response = await api.post('/schedule-publication-status/publish', {
      locationId,
      scheduleId,
      weekStartDate
    });
    shiftsListCache.clear();
    return response.data;
  },
  unpublish: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string }> => {
    const response = await api.post('/schedule-publication-status/unpublish', {
      locationId,
      scheduleId,
      weekStartDate
    });
    shiftsListCache.clear();
    return response.data;
  },
  markChanged: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string; id: number; changedAt: string }> => {
    const response = await api.post('/schedule-publication-status/mark-changed', {
      locationId,
      scheduleId,
      weekStartDate
    });
    shiftsListCache.clear();
    return response.data;
  },
};

// Attendance API
export const attendanceApi = {
  getAll: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    employeeId?: number;
    scheduleId?: number;
  }): Promise<AttendanceRecord[]> => {
    const key = paramsCacheKey('attendance', params as Record<string, unknown> | undefined);
    return attendanceListCache.get(key, async () => {
      const response = await api.get('/attendance', { params });
      return response.data;
    });
  },

  getCounts: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    employeeId?: number;
    scheduleId?: number;
  }): Promise<Array<{ date: string; count: number }>> => {
    const response = await api.get('/attendance/counts', { params });
    return response.data;
  },

  getScheduleCounts: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
  }): Promise<Array<{ scheduleId: number; count: number }>> => {
    const response = await api.get('/attendance/schedule-counts', { params });
    return response.data;
  },

  getHistory: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    employeeId?: number;
  }): Promise<{ data: AttendanceRecord[]; pagination?: any }> => {
    const response = await api.get('/attendance/history', { params });
    return response.data;
  },

  getById: async (id: number): Promise<AttendanceRecord> => {
    const response = await api.get(`/attendance/${id}`);
    return response.data;
  },

  /** Lazy-load clock-in photo (not included in main list for performance) */
  getClockInPhoto: async (id: number): Promise<string> => {
    const response = await api.get<{ photo: string }>(`/attendance/${id}/clock-in-photo`);
    return response.data.photo;
  },

  /** Lazy-load clock-out photo (not included in main list for performance) */
  getClockOutPhoto: async (id: number): Promise<string> => {
    const response = await api.get<{ photo: string }>(`/attendance/${id}/clock-out-photo`);
    return response.data.photo;
  },

  create: async (attendance: CreateAttendanceRecordRequest): Promise<AttendanceRecord> => {
    const response = await api.post('/attendance/manual', attendance);
    return response.data;
  },

  update: async (id: number, attendance: UpdateAttendanceRecordRequest): Promise<void> => {
    await api.patch(`/attendance/${id}`, attendance);
    attendanceListCache.clear();
  },

  delete: async (id: number, isHardDelete?: boolean): Promise<void> => {
    await api.delete(`/attendance/${id}`, {
      params: isHardDelete ? { isHardDelete: true } : {}
    });
  },

  importValidate: async (
    payload: {
      locationId: number;
      fileName?: string;
      mapping: AttendanceImportColumnMapping;
      rows: AttendanceImportRowInput[];
      /** Inclusive yyyy-MM-dd bounds; must match Attendance page week when set */
      allowedWeekStartDate?: string;
      allowedWeekEndDate?: string;
    },
    sourceFile?: File | null
  ): Promise<AttendanceImportValidateResponse> => {
    if (sourceFile) {
      const fd = new FormData();
      fd.append(
        'payload',
        JSON.stringify({
          locationId: payload.locationId,
          fileName: payload.fileName ?? sourceFile.name,
          mapping: payload.mapping,
          rows: payload.rows,
          ...(payload.allowedWeekStartDate && payload.allowedWeekEndDate
            ? {
                allowedWeekStartDate: payload.allowedWeekStartDate,
                allowedWeekEndDate: payload.allowedWeekEndDate,
              }
            : {}),
        })
      );
      fd.append('file', sourceFile, sourceFile.name);
      const response = await api.post('/attendance/import/validate', fd);
      return response.data;
    }
    const response = await api.post('/attendance/import/validate', payload);
    return response.data;
  },

  importDownloadUploadedFile: async (batchId: number): Promise<Blob> => {
    const response = await api.get(`/attendance/import/${batchId}/file`, { responseType: 'blob' });
    return response.data as Blob;
  },

  importCommit: async (batchId: number): Promise<AttendanceImportCommitResponse> => {
    const response = await api.post('/attendance/import/commit', { batchId });
    return response.data;
  },

  importBulkDelete: async (payload: {
    locationId: number;
    attendanceRecordIds: number[];
  }): Promise<AttendanceImportBulkDeleteResponse> => {
    const response = await api.post('/attendance/import/bulk-delete', payload);
    return response.data;
  },

  importHistory: async (
    locationId: number,
    options?: {
      page?: number;
      pageSize?: number;
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<AttendanceImportHistoryPage> => {
    const params: Record<string, string | number> = {
      locationId,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 20,
    };
    if (options?.fromDate) params.fromDate = options.fromDate;
    if (options?.toDate) params.toDate = options.toDate;
    const response = await api.get('/attendance/import/history', { params });
    return response.data;
  },

  importBatchDetail: async (batchId: number): Promise<AttendanceImportBatchDetail> => {
    const response = await api.get(`/attendance/import/${batchId}`);
    return response.data;
  },

  clockIn: async (
    employeeId: number,
    imageBase64?: string,
    timeStationId?: number,
    deviceId?: string,
    locationId?: number,
    skipFaceVerification?: boolean,
    scheduleId?: number
  ): Promise<{ success: boolean; message: string; attendanceRecordId: number; clockInTime: string; faceMatchConfidence?: number }> => {
    try {
      const response = await api.post('/attendance/clock-in', {
        employeeId,
        imageBase64,
        timeStationId,
        deviceId,
        locationId,
        scheduleId,
        skipFaceVerification: skipFaceVerification === true,
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        const msg = error.response.data.message || 'Failed to clock in';
        const detail = error.response.data.error;
        throw new Error(detail ? `${msg}: ${detail}` : msg);
      }
      throw error;
    }
  },

  clockOut: async (employeeId: number, imageBase64?: string, timeStationId?: number, deviceId?: string, locationId?: number, skipFaceVerification?: boolean): Promise<{ success: boolean; message: string; attendanceRecordId: number; clockInTime: string; clockOutTime: string; faceMatchConfidence?: number }> => {
    const response = await api.post('/attendance/clock-out', {
      employeeId,
      imageBase64,
      timeStationId,
      deviceId,
      locationId,
      skipFaceVerification: skipFaceVerification === true,
    });
    return response.data;
  },
};

// Face Recognition API
export const faceApi = {
  enroll: async (request: { employeeId: number; imageBase64: string }): Promise<{ success: boolean; message?: string; faceId?: string; profileImageUrl?: string }> => {
    const response = await api.post('/face-recognition/enroll', request);
    return response.data;
  },

  recognize: async (request: { imageBase64: string; timeStationId?: number }): Promise<{ success: boolean; employee?: { id: number; firstName: string; lastName: string; email: string }; confidence?: number; message?: string }> => {
    const response = await api.post('/face-recognition/recognize', request);
    return response.data;
  },

  /** Delete employee's face ID so they can enroll fresh for face scan attendance. */
  deleteFaceForEmployee: async (employeeId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/face-recognition/employee/${employeeId}/face`);
    return response.data;
  },

  getSyncStatus: async (): Promise<{
    collectionId?: string;
    databaseFaceCount: number;
    rekognitionFaceCount: number;
    isInSync: boolean;
    orphanedFacesCount: number;
    missingFacesCount: number;
    orphanedFaceIds: string[];
    missingFaceIds: string[];
    rekognitionAvailable?: boolean;
    rekognitionErrorMessage?: string;
  }> => {
    const response = await api.get('/face-recognition/sync-status');
    return response.data;
  },

  syncFaces: async (): Promise<{
    success: boolean;
    databaseFaceCount: number;
    rekognitionFaceCountBefore: number;
    rekognitionFaceCountAfter: number;
    orphanedFacesDeleted: number;
    missingFacesCleared: number;
    message: string;
  }> => {
    const response = await api.post('/face-recognition/sync');
    return response.data;
  },
};

// Task API
export const taskApi = {
  getAll: async (params?: { assignedToEmployeeId?: number; locationId?: number }): Promise<Task[]> => {
    const queryParams: Record<string, number> = {};
    if (params?.assignedToEmployeeId) queryParams.assignedToEmployeeId = params.assignedToEmployeeId;
    if (params?.locationId) queryParams.locationId = params.locationId;
    const response = await api.get('/tasks', { params: queryParams });
    return response.data;
  },

  /** Returns tasks visible to current user per permission matrix (JWT-based). */
  getMyTasks: async (): Promise<MyTask[]> => {
    const response = await api.get('/tasks/my-tasks');
    return response.data;
  },

  getById: async (id: number): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  create: async (task: CreateTaskRequest | FormData): Promise<Task> => {
    const config = task instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post('/tasks', task, config);
    return response.data;
  },

  update: async (id: number, task: UpdateTaskRequest | FormData): Promise<Task> => {
    const config = task instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.put(`/tasks/${id}`, task, config);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },

  getConversations: async (taskId: number): Promise<TaskConversation[]> => {
    const response = await api.get(`/tasks/${taskId}/conversations`);
    return response.data;
  },

  addConversation: async (taskId: number, request: AddConversationRequest | FormData): Promise<TaskConversation> => {
    console.log('[API] addConversation called');
    console.log('[API] Task ID:', taskId);
    console.log('[API] Request type:', request instanceof FormData ? 'FormData' : 'JSON');
    
    if (request instanceof FormData) {
      console.log('[API] FormData entries:');
      const entries = Array.from(request.entries());
      entries.forEach(([key, value]) => {
        if (value && typeof value === 'object' && 'name' in value && 'size' in value && 'type' in value) {
          const file = value as File;
          console.log(`[API]   ${key}: File(${file.name}, ${file.size} bytes, ${file.type})`);
        } else {
          console.log(`[API]   ${key}: ${value}`);
        }
      });
    } else {
      console.log('[API] JSON request:', request);
    }
    
    // For FormData, create a request without the default Content-Type header
    // For JSON, explicitly set Content-Type
    try {
      // The interceptor will handle removing Content-Type for FormData
      // For JSON, explicitly set Content-Type
      const config = request instanceof FormData 
        ? {} // Let interceptor handle FormData
        : { headers: { 'Content-Type': 'application/json' } };
      
      console.log('[API] Making POST request:', request instanceof FormData ? 'FormData' : 'JSON');
      const response = await api.post(`/tasks/${taskId}/conversations`, request, config);
      
      console.log('[API] ✅ Response received:', response);
      console.log('[API] Response status:', response.status);
      console.log('[API] Response data:', response.data);
      console.log('[API] Response data type:', typeof response.data);
      console.log('[API] Response data attachments:', response.data.attachments);
      console.log('[API] Response data attachments type:', typeof response.data.attachments);
      console.log('[API] Response data attachments is array:', Array.isArray(response.data.attachments));
      console.log('[API] Response data attachments count:', response.data.attachments?.length || 0);
      
      if (response.data.attachments && Array.isArray(response.data.attachments)) {
        response.data.attachments.forEach((att: any, idx: number) => {
          console.log(`[API]   Attachment ${idx + 1} in response:`, att);
        });
      } else {
        console.warn('[API] ⚠️ No attachments array in response data!');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('[API] ❌ Error in addConversation:', error);
      console.error('[API] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        request: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      if (error.response?.data) {
        console.error('[API] Full error response data:', JSON.stringify(error.response.data, null, 2));
        console.error('[API] Error response message:', error.response.data.message);
        console.error('[API] Error response error:', error.response.data.error);
      } else {
        console.error('[API] No response data in error');
      }
      throw error;
    }
  },
  addReaction: async (taskId: number, conversationId: number, reactionType: string): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.post(`/tasks/${taskId}/conversations/${conversationId}/reactions`, { ReactionType: reactionType });
    return response.data;
  },
  removeReaction: async (taskId: number, conversationId: number): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.delete(`/tasks/${taskId}/conversations/${conversationId}/reactions`);
    return response.data;
  },
  deleteAttachment: async (taskId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
  },
};

// SubTask API
export const subTaskApi = {
  getAll: async (taskId: number): Promise<SubTask[]> => {
    const response = await api.get(`/tasks/${taskId}/subtasks`);
    return response.data;
  },

  getById: async (taskId: number, id: number): Promise<SubTask> => {
    const response = await api.get(`/tasks/${taskId}/subtasks/${id}`);
    return response.data;
  },

  create: async (taskId: number, subTask: CreateSubTaskRequest | FormData): Promise<SubTask> => {
    const config = subTask instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post(`/tasks/${taskId}/subtasks`, subTask, config);
    return response.data;
  },

  update: async (taskId: number, id: number, subTask: UpdateSubTaskRequest | FormData): Promise<SubTask> => {
    const config = subTask instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.put(`/tasks/${taskId}/subtasks/${id}`, subTask, config);
    return response.data;
  },

  delete: async (taskId: number, id: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/subtasks/${id}`);
  },

  getConversations: async (taskId: number, subTaskId: number): Promise<SubTaskConversation[]> => {
    const response = await api.get(`/tasks/${taskId}/subtasks/${subTaskId}/conversations`);
    return response.data;
  },

  addConversation: async (taskId: number, subTaskId: number, request: AddConversationRequest | FormData): Promise<SubTaskConversation> => {
    const config = request instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post(`/tasks/${taskId}/subtasks/${subTaskId}/conversations`, request, config);
    return response.data;
  },

  addReaction: async (taskId: number, subTaskId: number, conversationId: number, reactionType: string): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.post(`/tasks/${taskId}/subtasks/${subTaskId}/conversations/${conversationId}/reactions`, { ReactionType: reactionType });
    return response.data;
  },

  removeReaction: async (taskId: number, subTaskId: number, conversationId: number): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.delete(`/tasks/${taskId}/subtasks/${subTaskId}/conversations/${conversationId}/reactions`);
    return response.data;
  },
  deleteAttachment: async (taskId: number, subTaskId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/subtasks/${subTaskId}/attachments/${attachmentId}`);
  },
};

// Message API
export const messageApi = {
  getInbox: async (params?: { locationId?: number; filter?: 'all' | 'unread' | 'direct' | 'groups' }): Promise<MessageInboxItem[]> => {
    const queryParams: Record<string, string | number> = {};
    if (params?.locationId) queryParams.locationId = params.locationId;
    if (params?.filter) queryParams.filter = params.filter;
    const response = await api.get('/messages/inbox', { params: queryParams });
    return response.data;
  },

  getUnreadInboxCount: async (locationId?: number): Promise<MessageInboxUnreadSummary> => {
    const params = locationId != null ? { locationId } : undefined;
    const response = await api.get('/messages/inbox/unread-count', { params });
    return {
      count: response.data.count ?? 0,
      otherLocationUnreadCount: response.data.otherLocationUnreadCount ?? 0,
      otherLocations: response.data.otherLocations ?? [],
    };
  },

  createDirect: async (data: CreateDirectMessageRequest): Promise<StaffMessage> => {
    const response = await api.post('/messages/direct', data);
    return response.data;
  },

  createGroup: async (data: CreateGroupChatRequest): Promise<StaffMessage> => {
    const response = await api.post('/messages/group', data);
    return response.data;
  },

  markRead: async (messageId: number): Promise<void> => {
    await api.put(`/messages/${messageId}/read`);
  },

  updatePinnedNote: async (messageId: number, pinnedNote: string | null): Promise<StaffMessage> => {
    const response = await api.put(`/messages/${messageId}/pinned-note`, { pinnedNote });
    return response.data;
  },

  getAll: async (params?: { locationId?: number }): Promise<StaffMessage[]> => {
    const queryParams: Record<string, number> = {};
    if (params?.locationId) queryParams.locationId = params.locationId;
    const response = await api.get('/messages', { params: queryParams });
    return response.data;
  },

  getMyMessages: async (): Promise<MyStaffMessage[]> => {
    const response = await api.get('/messages/my-messages');
    return response.data;
  },

  getById: async (id: number): Promise<StaffMessage> => {
    const response = await api.get(`/messages/${id}`);
    return response.data;
  },

  create: async (data: CreateMessageRequest | FormData): Promise<StaffMessage> => {
    const config = data instanceof FormData ? {} : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post('/messages', data, config);
    return response.data;
  },

  update: async (id: number, data: UpdateMessageRequest | FormData): Promise<StaffMessage> => {
    const config = data instanceof FormData ? {} : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.put(`/messages/${id}`, data, config);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/messages/${id}`);
  },

  deleteAttachment: async (messageId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/messages/${messageId}/attachments/${attachmentId}`);
  },

  getConversations: async (messageId: number): Promise<MessageConversation[]> => {
    const response = await api.get(`/messages/${messageId}/conversations`);
    return response.data;
  },

  addConversation: async (messageId: number, request: AddConversationRequest | FormData): Promise<MessageConversation> => {
    const config = request instanceof FormData ? {} : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post(`/messages/${messageId}/conversations`, request, config);
    return response.data;
  },

  addReaction: async (messageId: number, conversationId: number, reactionType: string): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.post(`/messages/${messageId}/conversations/${conversationId}/reactions`, { reactionType });
    return response.data;
  },

  removeReaction: async (messageId: number, conversationId: number): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.delete(`/messages/${messageId}/conversations/${conversationId}/reactions`);
    return response.data;
  },
};

// ============================================
// LEAVE MANAGEMENT APIs
// ============================================

// PayModel API
export const payModelApi = {
  getAll: async (): Promise<PayModel[]> => {
    const response = await api.get('/paymodels');
    return response.data;
  },
  
  getById: async (id: number): Promise<PayModel> => {
    const response = await api.get(`/paymodels/${id}`);
    return response.data;
  },
  
  create: async (payModel: CreatePayModelRequest): Promise<PayModel> => {
    const response = await api.post('/paymodels', payModel);
    return response.data;
  },
  
  update: async (id: number, payModel: UpdatePayModelRequest): Promise<void> => {
    await api.put(`/paymodels/${id}`, payModel);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/paymodels/${id}`);
  },
};

// LeaveType API
export const leaveTypeApi = {
  getAll: async (): Promise<LeaveType[]> => {
    const response = await api.get('/leavetypes');
    return response.data;
  },
  
  getById: async (id: number): Promise<LeaveType> => {
    const response = await api.get(`/leavetypes/${id}`);
    return response.data;
  },
  
  create: async (leaveType: CreateLeaveTypeRequest): Promise<LeaveType> => {
    const response = await api.post('/leavetypes', leaveType);
    return response.data;
  },
  
  update: async (id: number, leaveType: UpdateLeaveTypeRequest): Promise<void> => {
    await api.put(`/leavetypes/${id}`, leaveType);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/leavetypes/${id}`);
  },
};

// Leave Ledger API (for timecards and payroll)
export const leaveLedgerApi = {
  getByEmployeeAndDateRange: async (
    employeeId: number,
    startDate: string,
    endDate: string
  ): Promise<LeaveLedger[]> => {
    const response = await api.get('/leave-ledgers', {
      params: { employeeId, startDate, endDate },
    });
    return response.data;
  },

  /** Report: all leave ledger entries in date range (includes employee name) */
  getReport: async (startDate: string, endDate: string): Promise<LeaveLedgerReportEntry[]> => {
    const response = await api.get('/leave-ledgers/report', {
      params: { startDate, endDate },
    });
    return response.data;
  },
};

// Employee Leave Balance API
export const employeeLeaveBalanceApi = {
  getAll: async (employeeId?: number, leaveYear?: number): Promise<EmployeeLeaveBalance[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (leaveYear) params.leaveYear = leaveYear;
    const response = await api.get('/employee-leave-balances', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeLeaveBalance> => {
    const response = await api.get(`/employee-leave-balances/${id}`);
    return response.data;
  },
  
  create: async (balance: CreateEmployeeLeaveBalanceRequest): Promise<EmployeeLeaveBalance> => {
    const response = await api.post('/employee-leave-balances', balance);
    return response.data;
  },
  
  update: async (id: number, balance: UpdateEmployeeLeaveBalanceRequest): Promise<void> => {
    await api.put(`/employee-leave-balances/${id}`, balance);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-leave-balances/${id}`);
  },
};

// Leave Policies API
export const leavePoliciesApi = {
  getAll: async (): Promise<LeavePolicy[]> => {
    const response = await api.get('/leavepolicies');
    return response.data;
  },
  
  getById: async (id: number): Promise<LeavePolicy> => {
    const response = await api.get(`/leavepolicies/${id}`);
    return response.data;
  },
  
  create: async (policy: CreateLeavePolicyRequest): Promise<LeavePolicy> => {
    const response = await api.post('/leavepolicies', policy);
    return response.data;
  },
  
  update: async (id: number, policy: UpdateLeavePolicyRequest): Promise<void> => {
    await api.put(`/leavepolicies/${id}`, policy);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/leavepolicies/${id}`);
  },
  
  addLeaveType: async (policyId: number, leaveType: CreateLeavePolicyTypeRequest): Promise<{ leavePolicyTypeId: number; message: string }> => {
    const response = await api.post(`/leavepolicies/${policyId}/leavetypes`, leaveType);
    return response.data;
  },
  
  updateLeaveType: async (policyId: number, leaveTypeId: number, leaveType: UpdateLeavePolicyTypeRequest): Promise<void> => {
    await api.put(`/leavepolicies/${policyId}/leavetypes/${leaveTypeId}`, leaveType);
  },
  
  removeLeaveType: async (policyId: number, leaveTypeId: number): Promise<void> => {
    await api.delete(`/leavepolicies/${policyId}/leavetypes/${leaveTypeId}`);
  },
};

// Employee Sick Leave Profile API
export const employeeSickLeaveProfileApi = {
  getAll: async (employeeId?: number): Promise<EmployeeSickLeaveProfile[]> => {
    const params = employeeId ? { employeeId } : {};
    const response = await api.get('/employee-sick-leave-profiles', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeSickLeaveProfile> => {
    const response = await api.get(`/employee-sick-leave-profiles/${id}`);
    return response.data;
  },
  
  getByEmployeeId: async (employeeId: number): Promise<EmployeeSickLeaveProfile> => {
    const response = await api.get(`/employee-sick-leave-profiles/employee/${employeeId}`);
    return response.data;
  },
  
  create: async (profile: CreateEmployeeSickLeaveProfileRequest): Promise<EmployeeSickLeaveProfile> => {
    const response = await api.post('/employee-sick-leave-profiles', profile);
    return response.data;
  },
  
  update: async (id: number, profile: UpdateEmployeeSickLeaveProfileRequest): Promise<void> => {
    await api.put(`/employee-sick-leave-profiles/${id}`, profile);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-sick-leave-profiles/${id}`);
  },
};

// Sick Leave Request API
export const sickLeaveRequestApi = {
  getAll: async (employeeId?: number, status?: string): Promise<SickLeaveRequest[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (status) params.status = status;
    const response = await api.get('/sick-leave-requests', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<SickLeaveRequest> => {
    const response = await api.get(`/sick-leave-requests/${id}`);
    return response.data;
  },
  
  create: async (request: CreateSickLeaveRequestRequest): Promise<SickLeaveRequest> => {
    const response = await api.post('/sick-leave-requests', request);
    return response.data;
  },
  
  update: async (id: number, request: UpdateSickLeaveRequestRequest): Promise<void> => {
    await api.put(`/sick-leave-requests/${id}`, request);
  },
  
  approve: async (id: number, request?: ApproveSickLeaveRequestRequest): Promise<void> => {
    await api.put(`/sick-leave-requests/${id}/approve`, request || {});
  },
  
  reject: async (id: number, request: RejectSickLeaveRequestRequest): Promise<void> => {
    await api.put(`/sick-leave-requests/${id}/reject`, request);
  },

  uploadCertificate: async (id: number, file: File): Promise<SickLeaveRequest> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/sick-leave-requests/${id}/certificate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadCertificate: async (id: number, disposition: 'inline' | 'attachment' = 'inline'): Promise<Blob> => {
    const response = await api.get(`/sick-leave-requests/${id}/certificate`, {
      params: { disposition },
      responseType: 'blob',
    });
    return response.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/sick-leave-requests/${id}`);
  },
};

// Jobs API
export const jobsApi = {
  getAll: async (): Promise<Job[]> => {
    const response = await api.get('/jobs');
    return response.data;
  },

  getById: async (id: number): Promise<Job> => {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },

  create: async (job: CreateJobRequest): Promise<Job> => {
    const response = await api.post('/jobs', job);
    return response.data;
  },

  update: async (id: number, job: UpdateJobRequest): Promise<void> => {
    await api.put(`/jobs/${id}`, job);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/jobs/${id}`);
  },
};

// Job Schedules API
export const jobSchedulesApi = {
  getAll: async (jobId: number): Promise<JobSchedule[]> => {
    const response = await api.get(`/jobs/${jobId}/schedules`);
    return response.data;
  },

  getById: async (jobId: number, scheduleId: number): Promise<JobSchedule> => {
    const response = await api.get(`/jobs/${jobId}/schedules/${scheduleId}`);
    return response.data;
  },

  create: async (jobId: number, schedule: CreateJobScheduleRequest): Promise<JobSchedule> => {
    const response = await api.post(`/jobs/${jobId}/schedules`, schedule);
    return response.data;
  },

  update: async (jobId: number, scheduleId: number, schedule: UpdateJobScheduleRequest): Promise<void> => {
    await api.put(`/jobs/${jobId}/schedules/${scheduleId}`, schedule);
  },

  delete: async (jobId: number, scheduleId: number): Promise<void> => {
    await api.delete(`/jobs/${jobId}/schedules/${scheduleId}`);
  },
};

// Job Steps API
export const jobStepsApi = {
  getAll: async (jobId: number): Promise<JobStep[]> => {
    const response = await api.get(`/jobs/${jobId}/steps`);
    return response.data;
  },

  getById: async (jobId: number, stepId: number): Promise<JobStep> => {
    const response = await api.get(`/jobs/${jobId}/steps/${stepId}`);
    return response.data;
  },

  create: async (jobId: number, step: CreateJobStepRequest): Promise<JobStep> => {
    const response = await api.post(`/jobs/${jobId}/steps`, step);
    return response.data;
  },

  update: async (jobId: number, stepId: number, step: UpdateJobStepRequest): Promise<void> => {
    await api.put(`/jobs/${jobId}/steps/${stepId}`, step);
  },

  delete: async (jobId: number, stepId: number): Promise<void> => {
    await api.delete(`/jobs/${jobId}/steps/${stepId}`);
  },

  execute: async (jobId: number, stepId: number): Promise<{ message: string }> => {
    const response = await api.post(`/jobs/${jobId}/steps/${stepId}/execute`);
    return response.data;
  },
};

// Job Monitor API
export const jobMonitorApi = {
  getRecurringJobs: async (): Promise<RecurringJob[]> => {
    const response = await api.get('/job-monitor/recurring-jobs');
    return response.data;
  },

  triggerJob: async (jobId: string): Promise<{ message: string }> => {
    const response = await api.post(`/job-monitor/trigger/${jobId}`);
    return response.data;
  },

  getJobRuns: async (holidayId?: number, page: number = 1, pageSize: number = 20): Promise<{
    data: PublicHolidayJobRun[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params: any = { page, pageSize };
    if (holidayId) params.holidayId = holidayId;
    const response = await api.get('/job-monitor/job-runs', { params });
    return response.data;
  },

  getJobLogs: async (jobRunId: number): Promise<PublicHolidayJobLog[]> => {
    const response = await api.get(`/job-monitor/job-runs/${jobRunId}/logs`);
    return response.data;
  },

  getJobRunHistory: async (jobId?: number, page: number = 1, pageSize: number = 20): Promise<{
    data: JobRunHistory[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params: any = { page, pageSize };
    if (jobId) params.jobId = jobId;
    const response = await api.get('/job-monitor/job-run-history', { params });
    return response.data;
  },

  getJobStepRunHistory: async (jobRunHistoryId: number): Promise<JobStepRunHistory[]> => {
    const response = await api.get(`/job-monitor/job-run-history/${jobRunHistoryId}/step-runs`);
    return response.data;
  },
};

// Employee Public Holiday API
export const employeePublicHolidayApi = {
  getAll: async (employeeId?: number, holidayId?: number, year?: number): Promise<EmployeePublicHoliday[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (holidayId) params.holidayId = holidayId;
    if (year) params.year = year;
    const response = await api.get('/employee-public-holidays', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeePublicHoliday> => {
    const response = await api.get(`/employee-public-holidays/${id}`);
    return response.data;
  },
  
  checkEligibility: async (request: CheckEligibilityRequest): Promise<any> => {
    const response = await api.post('/employee-public-holidays/check-eligibility', request);
    return response.data;
  },
  
  create: async (request: CreateEmployeePublicHolidayRequest): Promise<EmployeePublicHoliday> => {
    const response = await api.post('/employee-public-holidays', request);
    return response.data;
  },
  
  update: async (id: number, request: UpdateEmployeePublicHolidayRequest): Promise<void> => {
    await api.put(`/employee-public-holidays/${id}`, request);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-public-holidays/${id}`);
  },
};

// Timecard API
export const timecardApi = {
  getAll: async (employeeId?: number, weekStartDate?: string, locationId?: number): Promise<Timecard[]> => {
    const params: Record<string, number | string> = {};
    if (employeeId) params.employeeId = employeeId;
    if (weekStartDate) params.weekStartDate = weekStartDate;
    if (locationId !== undefined && locationId !== null && locationId !== 0) params.locationId = locationId;
    const response = await api.get('/timecards', { params });
    return response.data;
  },
  
  getByEmployeeAndWeek: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<Timecard | null> => {
    try {
      const params: Record<string, number> = {};
      if (locationId !== undefined && locationId !== null && locationId !== 0) {
        params.locationId = locationId;
      }
      const response = await api.get(`/timecards/employee/${employeeId}/week/${weekStartDate}`, { params });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getWeeklyPremiumHours: async (
    employeeId: number,
    weekStartDate: string,
  ): Promise<{ sundayPremiumHours: number; bhPremiumHours: number }> => {
    try {
      const response = await api.get(`/timecards/employee/${employeeId}/week/${weekStartDate}/premium-hours`);
      return {
        sundayPremiumHours: Number(response.data?.sundayPremiumHours ?? 0),
        bhPremiumHours: Number(response.data?.bhPremiumHours ?? 0),
      };
    } catch {
      return { sundayPremiumHours: 0, bhPremiumHours: 0 };
    }
  },

  getLocationTotalsForEmployeeAndWeek: async (employeeId: number, weekStartDate: string, excludeLocationId?: number): Promise<Array<{ locationId: number | null; locationName: string; totalHours: number }>> => {
    try {
      const params = excludeLocationId != null ? { excludeLocationId } : {};
      const url = `/timecards/employee/${employeeId}/week/${weekStartDate}/location-totals`;
      console.log('[LocationTotals API] GET', url, params);
      const response = await api.get(url, { params });
      const totals = response.data?.totals ?? [];
      if (response.data?.debug) {
        console.warn('[LocationTotals API] Debug:', response.data.debug);
      }
      console.log('[LocationTotals API] Response:', response.status, 'totals=', totals);
      return totals;
    } catch (err: any) {
      console.error('[LocationTotals API] Error:', err?.response?.status, err?.message, err?.response?.data);
      return [];
    }
  },
  
  create: async (request: CreateTimecardRequest): Promise<Timecard> => {
    const response = await api.post('/timecards', request);
    return response.data;
  },
  
  addDay: async (timecardId: number, request: { date: string; mode?: string; extras?: number; totalHours?: number }): Promise<{ id: number; date: string; mode: string }> => {
    const response = await api.post(`/timecards/${timecardId}/day`, request);
    return response.data;
  },
  
  updateDay: async (timecardId: number, dayId: number, request: UpdateTimecardDayRequest): Promise<void> => {
    await api.put(`/timecards/${timecardId}/day/${dayId}`, request);
  },

  deleteDay: async (timecardId: number, dayId: number): Promise<void> => {
    await api.delete(`/timecards/${timecardId}/day/${dayId}`);
  },

  markAbsent: async (employeeId: number, date: string, locationId?: number): Promise<TimecardAbsencePay[]> => {
    const response = await api.post('/timecards/absence/mark-absent', { employeeId, date, locationId });
    return response.data;
  },

  markSick: async (employeeId: number, date: string, locationId?: number): Promise<TimecardAbsencePay[]> => {
    const response = await api.post('/timecards/absence/mark-sick', { employeeId, date, locationId });
    return response.data;
  },

  approveAbsencePayroll: async (employeeId: number, date: string, locationId?: number): Promise<TimecardAbsencePay[]> => {
    const response = await api.put('/timecards/absence/approve-payroll', { employeeId, date, locationId });
    return response.data;
  },

  overrideAbsencePay: async (employeeId: number, date: string, locationId: number | undefined, amount: number): Promise<TimecardAbsencePay[]> => {
    const response = await api.put('/timecards/absence/override', { employeeId, date, locationId, amount }, { timeout: 120000 });
    return response.data;
  },

  setAbsencePayrollNote: async (employeeId: number, date: string, locationId: number | undefined, note: string): Promise<TimecardAbsencePay[]> => {
    const response = await api.put('/timecards/absence/note', { employeeId, date, locationId, note });
    return response.data;
  },
  
  sync: async (employeeId: number, weekStartDate: string, locationId?: number, hardSync?: boolean): Promise<Timecard> => {
    const body: any = { employeeId, weekStartDate };
    if (locationId !== undefined && locationId !== null && locationId !== 0) {
      body.locationId = locationId;
    }
    if (hardSync) {
      body.hardSync = true;
    }
    const response = await api.post('/timecards/sync', body);
    return response.data;
  },
  
  syncStep1: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<{ 
    success: boolean; 
    message: string; 
    shifts: Array<{ shiftId: number; date: string; inTime: string; outTime: string; breakMins: number | null; breakPaid: boolean; breakUseGlobalSettings?: boolean }>;
    attendanceRecords: Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null; adjustedInTime: string | null; adjustedOutTime: string | null }>;
    step3Records: Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null }>;
    step4Records: Array<{ shiftId: number; shiftDate: string; shiftInTime: string; shiftOutTime: string; shiftBreakMins: number; shiftBreakPaid: boolean; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>;
    step5Records: Array<{ shiftId: number | null; shiftDate: string | null; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>;
    step7Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string }>;
    step8Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string }>;
    step9Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null }>;
    step10Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null; totalHours: number | null; status: string }>;
  }> => {
    const body: any = { employeeId, weekStartDate };
    if (locationId !== undefined && locationId !== null && locationId !== 0) {
      body.locationId = locationId;
    }
    const response = await api.post('/timecards/sync-step1', body);
    return response.data;
  },
  
  checkSyncNeeded: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<{ syncNeeded: boolean; reason: string }> => {
    const params: Record<string, number | string> = { employeeId, weekStartDate };
    if (locationId !== undefined && locationId !== null && locationId !== 0) {
      params.locationId = locationId;
    }
    const response = await api.get('/timecards/check-sync-needed', { params });
    return response.data;
  },

  approve: async (id: number): Promise<void> => {
    await api.post(`/timecards/${id}/approve`);
  },

  unapprove: async (id: number): Promise<void> => {
    await api.post(`/timecards/${id}/unapprove`);
  },

  updateNotes: async (id: number, notes: string | null): Promise<void> => {
    await api.patch(`/timecards/${id}/notes`, { notes: notes ?? '' });
  },
};

// Audit Log Config API
export const auditLogConfigApi = {
  getAll: async (): Promise<AuditLogConfig[]> => {
    const response = await api.get('/audit-log-configs');
    return response.data;
  },

  create: async (request: {
    entity: string;
    isActive: boolean;
    actions: string[];
    fields: AuditLogConfigFieldRequest[];
  }): Promise<{ id: number }> => {
    const response = await api.post('/audit-log-configs', request);
    return response.data;
  },

  update: async (id: number, request: {
    entity: string;
    isActive: boolean;
    actions: string[];
    fields: AuditLogConfigFieldRequest[];
  }): Promise<void> => {
    await api.put(`/audit-log-configs/${id}`, request);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/audit-log-configs/${id}`);
  },

  activate: async (id: number): Promise<void> => {
    await api.put(`/audit-log-configs/${id}/activate`);
  },

  deactivate: async (id: number): Promise<void> => {
    await api.put(`/audit-log-configs/${id}/deactivate`);
  },
};

// Audit Logs API
export const auditLogApi = {
  getAll: async (params?: {
    entity?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    data: AuditLogEvent[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const response = await api.get('/audit-logs', { params });
    return response.data;
  },
};

/** Coalesce identical in-flight list calls (StrictMode remounts fire list twice). */
const payrollRunsListInflight = new Map<number, Promise<PayrollRun[]>>();

// ─────────────────────────────────────────────────────────────────────────────
export const payrollRunApi = {
  list: async (take = 50): Promise<PayrollRun[]> => {
    const existing = payrollRunsListInflight.get(take);
    if (existing) return existing;

    const request = api
      .get<PayrollRun[]>('/payroll-runs', { params: { take } })
      .then((response) => response.data)
      .finally(() => {
        if (payrollRunsListInflight.get(take) === request) {
          payrollRunsListInflight.delete(take);
        }
      });

    payrollRunsListInflight.set(take, request);
    return request;
  },

  create: async (body: CreatePayrollRunRequest): Promise<PayrollRun> => {
    const response = await api.post<PayrollRun>('/payroll-runs', body);
    return response.data;
  },

  delete: async (runId: number): Promise<void> => {
    await api.delete(`/payroll-runs/${runId}`);
  },

  deleteAllDrafts: async (): Promise<{ deletedCount: number }> => {
    const response = await api.delete<{ deletedCount: number }>('/payroll-runs/drafts/all');
    return response.data;
  },

  preview: async (runId: number): Promise<PayrollRunPreviewDto> => {
    const response = await api.get<PayrollRunPreviewDto>(`/payroll-runs/${runId}/preview`);
    return response.data;
  },

  reprocess: async (runId: number): Promise<PayrollRun> => {
    const response = await api.post<PayrollRun>(`/payroll-runs/${runId}/reprocess`);
    return response.data;
  },

  audit: async (
    runId: number,
    params?: {
      section?: 'summary' | 'included' | 'excluded';
      skip?: number;
      take?: number;
      employeeId?: number;
    },
  ): Promise<PayrollRunAuditDto> => {
    const response = await api.get<PayrollRunAuditDto>(`/payroll-runs/${runId}/audit`, {
      params: {
        section: params?.section ?? 'summary',
        skip: params?.skip ?? 0,
        take: params?.take ?? 20,
        ...(params?.employeeId != null ? { employeeId: params.employeeId } : {}),
      },
      timeout: 120000,
    });
    return response.data;
  },

  finalize: async (runId: number, force = false): Promise<PayrollRun> => {
    const response = await api.post<PayrollRun>(`/payroll-runs/${runId}/finalize`, { force });
    return response.data;
  },

  exportSickPaySspCsv: async (runId: number): Promise<Blob> => {
    const response = await api.get(`/payroll-runs/${runId}/export/sick-pay-ssp-csv`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  variance: async (runId: number): Promise<PayrollRunVarianceDto> => {
    const response = await api.get<PayrollRunVarianceDto>(`/payroll-runs/${runId}/variance`);
    return response.data;
  },

  oohPeriodPresets: async (asOf?: string): Promise<{
    asOf: string;
    hourlyCycle: { periodStart: string; periodEnd: string; label: string };
    salaryMonth: { periodStart: string; periodEnd: string; label: string };
  }> => {
    const response = await api.get('/payroll-runs/ooh-period-presets', {
      params: asOf ? { asOf } : undefined,
    });
    return response.data;
  },
};

export interface PayrollRunVarianceRowDto {
  employeeId: number;
  employeeName: string;
  projectedGross: number;
  actualGross: number;
  delta: number;
  notes?: string | null;
}

export interface PayrollRunVarianceDto {
  runId: number;
  periodStart: string;
  periodEnd: string;
  projectedLabel: string;
  actualLabel: string;
  rows: PayrollRunVarianceRowDto[];
  totalProjected: number;
  totalActual: number;
  totalDelta: number;
}

//  Early / Late report runs (server-persisted snapshots)
// ─────────────────────────────────────────────────────────────────────────────

export interface EarlyLateReportRunListDto {
  id: number;
  createdAt: string;
  fromDate: string;
  toDate: string;
  currencyLabel: string;
  recordsProcessed: number;
  totalAmount: number;
  /** Parsed from snapshot JSON; legacy rows without property imply 5 on the server. */
  netMinutesMin: number;
}

export interface EarlyLateReportSnapshotPayload {
  headers: string[];
  dataRows: unknown[];
  currencyLabel?: string;
  netMinutesMin?: number;
  /** Filter criteria saved with the run (status, schedule, position, employee, dates). */
  parameters?: Record<string, unknown>;
}

export interface SaveEarlyLateReportRequest {
  fromDate: string;
  toDate: string;
  currencyLabel: string;
  recordsProcessed: number;
  totalAmount: number;
  snapshot: EarlyLateReportSnapshotPayload;
}

export interface EarlyLateReportRunDetailDto extends EarlyLateReportRunListDto {
  snapshot?: EarlyLateReportSnapshotPayload;
}

// Employee Details report — saved templates (filters + columns + status, per user, tenant DB)
// ─────────────────────────────────────────────────────────────────────────────

export interface EmployeeReportTemplateDto {
  id: number;
  /** Employee who created the template; list is shared across users with Reports access. */
  employeeId?: number;
  name: string;
  statusFilter: string;
  filtersJson: string;
  columnsJson: string;
  updatedAt: string;
}

export const employeeReportTemplatesApi = {
  list: async (): Promise<EmployeeReportTemplateDto[]> => {
    const response = await api.get<EmployeeReportTemplateDto[]>('/employee-report-templates');
    return response.data;
  },
  get: async (id: number): Promise<EmployeeReportTemplateDto> => {
    const response = await api.get<EmployeeReportTemplateDto>(`/employee-report-templates/${id}`);
    return response.data;
  },
  create: async (body: {
    name: string;
    statusFilter: string;
    filters: unknown[];
    columns: { fieldKey: string; headerLabel: string }[];
  }): Promise<EmployeeReportTemplateDto> => {
    const response = await api.post<EmployeeReportTemplateDto>('/employee-report-templates', body);
    return response.data;
  },
  update: async (
    id: number,
    body: {
      name: string;
      statusFilter: string;
      filters: unknown[];
      columns: { fieldKey: string; headerLabel: string }[];
    }
  ): Promise<EmployeeReportTemplateDto> => {
    const response = await api.put<EmployeeReportTemplateDto>(`/employee-report-templates/${id}`, body);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-report-templates/${id}`);
  },
};

export interface PlanVsActualReportEmployeeRow {
  id: number;
  firstName: string;
  lastName: string;
  isActive: boolean;
  mainLocationId: number;
}

export interface PlanVsActualWeekReportResponse {
  weekStartDate: string;
  weekStartDisplay: string;
  weekEndDisplay: string;
  locationId?: number | null;
  schedules: Schedule[];
  shifts: Shift[];
  timecards: Timecard[];
  employees: PlanVsActualReportEmployeeRow[];
  payRecords: PayRecord[];
}

/** Coalesce identical in-flight week report requests (StrictMode remounts, parallel callers). */
const planVsActualInflight = new Map<string, Promise<PlanVsActualWeekReportResponse>>();

function planVsActualRequestKey(params: {
  weekStartDate: string;
  weekStartDisplay: string;
  weekEndDisplay: string;
  locationId?: number;
}): string {
  return [
    params.weekStartDate,
    params.weekStartDisplay,
    params.weekEndDisplay,
    params.locationId ?? '',
  ].join('|');
}

export const planVsActualReportApi = {
  getWeekReport: async (params: {
    weekStartDate: string;
    weekStartDisplay: string;
    weekEndDisplay: string;
    locationId?: number;
  }): Promise<PlanVsActualWeekReportResponse> => {
    const key = planVsActualRequestKey(params);
    const existing = planVsActualInflight.get(key);
    if (existing) return existing;

    const request = api
      .get<PlanVsActualWeekReportResponse>('/reports/plan-vs-actual', { params })
      .then((response) => response.data)
      .finally(() => {
        if (planVsActualInflight.get(key) === request) {
          planVsActualInflight.delete(key);
        }
      });

    planVsActualInflight.set(key, request);
    return request;
  },
};

export const earlyLateReportApi = {
  list: async (take = 80): Promise<EarlyLateReportRunListDto[]> => {
    const response = await api.get<EarlyLateReportRunListDto[]>('/early-late-reports', {
      params: { take },
    });
    return response.data;
  },

  get: async (id: number): Promise<EarlyLateReportRunDetailDto> => {
    const response = await api.get<EarlyLateReportRunDetailDto>(`/early-late-reports/${id}`);
    return response.data;
  },

  create: async (body: SaveEarlyLateReportRequest): Promise<EarlyLateReportRunListDto> => {
    const response = await api.post<EarlyLateReportRunListDto>('/early-late-reports', body);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/early-late-reports/${id}`);
  },
};

// Employee Details report runs (server-persisted snapshots, per user)
// ─────────────────────────────────────────────────────────────────────────────

export interface EmployeeDetailsReportRunListDto {
  id: number;
  createdAt: string;
  recordsProcessed: number;
  parameters: Record<string, unknown>;
}

export interface EmployeeDetailsReportSnapshotPayload {
  headers: string[];
  dataRows: string[][];
}

export interface SaveEmployeeDetailsReportRunRequest {
  recordsProcessed: number;
  parameters: Record<string, unknown>;
  snapshot: EmployeeDetailsReportSnapshotPayload;
}

export interface EmployeeDetailsReportRunDetailDto extends EmployeeDetailsReportRunListDto {
  snapshot: EmployeeDetailsReportSnapshotPayload;
}

export const employeeDetailsReportRunsApi = {
  list: async (take = 80): Promise<EmployeeDetailsReportRunListDto[]> => {
    const response = await api.get<EmployeeDetailsReportRunListDto[]>('/employee-details-report-runs', {
      params: { take },
    });
    return response.data;
  },

  get: async (id: number): Promise<EmployeeDetailsReportRunDetailDto> => {
    const response = await api.get<EmployeeDetailsReportRunDetailDto>(`/employee-details-report-runs/${id}`);
    return response.data;
  },

  create: async (body: SaveEmployeeDetailsReportRunRequest): Promise<EmployeeDetailsReportRunDetailDto> => {
    const response = await api.post<EmployeeDetailsReportRunDetailDto>('/employee-details-report-runs', body);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-details-report-runs/${id}`);
  },
};

// Invoice report runs (monthly schedule invoice snapshots)
// ─────────────────────────────────────────────────────────────────────────────

export interface InvoiceReportRunListDto {
  id: number;
  createdAt: string;
  scheduleId: number;
  scheduleName: string;
  month: string;
  locationId?: number | null;
  recordsProcessed: number;
  grandTotalHours: number;
  parameters: Record<string, unknown>;
}

export interface SaveInvoiceReportRunRequest {
  scheduleId: number;
  scheduleName: string;
  month: string;
  locationId?: number | null;
  recordsProcessed: number;
  grandTotalHours: number;
  parameters: Record<string, unknown>;
  snapshot: Record<string, unknown>;
}

export interface InvoiceReportRunDetailDto extends InvoiceReportRunListDto {
  snapshot: Record<string, unknown>;
}

export const invoiceReportRunsApi = {
  list: async (take = 80): Promise<InvoiceReportRunListDto[]> => {
    const response = await api.get<InvoiceReportRunListDto[]>('/invoice-report-runs', { params: { take } });
    return response.data;
  },

  get: async (id: number): Promise<InvoiceReportRunDetailDto> => {
    const response = await api.get<InvoiceReportRunDetailDto>(`/invoice-report-runs/${id}`);
    return response.data;
  },

  create: async (body: SaveInvoiceReportRunRequest): Promise<InvoiceReportRunDetailDto> => {
    const response = await api.post<InvoiceReportRunDetailDto>('/invoice-report-runs', body);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/invoice-report-runs/${id}`);
  },
};

// Payroll Details report — saved templates + run history
// ─────────────────────────────────────────────────────────────────────────────

export interface PayrollReportTemplateDto {
  id: number;
  employeeId?: number;
  name: string;
  layout: string;
  filtersJson: string;
  columnsJson: string;
  isDefault?: boolean;
  updatedAt: string;
}

export const payrollReportTemplatesApi = {
  list: async (): Promise<PayrollReportTemplateDto[]> => {
    const response = await api.get<PayrollReportTemplateDto[]>('/payroll-report-templates');
    return response.data;
  },
  get: async (id: number): Promise<PayrollReportTemplateDto> => {
    const response = await api.get<PayrollReportTemplateDto>(`/payroll-report-templates/${id}`);
    return response.data;
  },
  create: async (body: {
    name: string;
    layout: string;
    filters: Record<string, unknown>;
    columns: { fieldKey: string; headerLabel: string }[];
    isDefault?: boolean;
  }): Promise<PayrollReportTemplateDto> => {
    const response = await api.post<PayrollReportTemplateDto>('/payroll-report-templates', body);
    return response.data;
  },
  update: async (
    id: number,
    body: {
      name: string;
      layout: string;
      filters: Record<string, unknown>;
      columns: { fieldKey: string; headerLabel: string }[];
      isDefault?: boolean;
    }
  ): Promise<PayrollReportTemplateDto> => {
    const response = await api.put<PayrollReportTemplateDto>(`/payroll-report-templates/${id}`, body);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/payroll-report-templates/${id}`);
  },
};

export interface PayrollDetailsReportRunListDto {
  id: number;
  createdAt: string;
  recordsProcessed: number;
  parameters: Record<string, unknown>;
}

export interface PayrollDetailsReportSnapshotPayload {
  headers: string[];
  dataRows: string[][];
}

export interface SavePayrollDetailsReportRunRequest {
  recordsProcessed: number;
  parameters: Record<string, unknown>;
  snapshot: PayrollDetailsReportSnapshotPayload;
}

export interface PayrollDetailsReportRunDetailDto extends PayrollDetailsReportRunListDto {
  snapshot: PayrollDetailsReportSnapshotPayload;
}

export const payrollDetailsReportRunsApi = {
  list: async (take = 80): Promise<PayrollDetailsReportRunListDto[]> => {
    const response = await api.get<PayrollDetailsReportRunListDto[]>('/payroll-details-report-runs', {
      params: { take },
    });
    return response.data;
  },
  get: async (id: number): Promise<PayrollDetailsReportRunDetailDto> => {
    const response = await api.get<PayrollDetailsReportRunDetailDto>(`/payroll-details-report-runs/${id}`);
    return response.data;
  },
  create: async (body: SavePayrollDetailsReportRunRequest): Promise<PayrollDetailsReportRunDetailDto> => {
    const response = await api.post<PayrollDetailsReportRunDetailDto>('/payroll-details-report-runs', body);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/payroll-details-report-runs/${id}`);
  },

  deleteAll: async (): Promise<{ deletedCount: number }> => {
    const response = await api.delete<{ deletedCount: number }>('/payroll-details-report-runs/all');
    return response.data;
  },
};

// Payroll Builder report — saved templates (employee filters + payroll columns)
// ─────────────────────────────────────────────────────────────────────────────

export interface PayrollBuilderReportTemplateDto {
  id: number;
  employeeId?: number;
  name: string;
  statusFilter: string;
  layout: string;
  employeeFiltersJson: string;
  reportOptionsJson: string;
  columnsJson: string;
  isDefault?: boolean;
  updatedAt: string;
}

export const payrollBuilderReportTemplatesApi = {
  list: async (): Promise<PayrollBuilderReportTemplateDto[]> => {
    const response = await api.get<PayrollBuilderReportTemplateDto[]>('/payroll-builder-report-templates');
    return response.data;
  },
  get: async (id: number): Promise<PayrollBuilderReportTemplateDto> => {
    const response = await api.get<PayrollBuilderReportTemplateDto>(`/payroll-builder-report-templates/${id}`);
    return response.data;
  },
  create: async (body: {
    name: string;
    statusFilter: string;
    layout: string;
    employeeFilters: unknown[];
    reportOptions: Record<string, unknown>;
    columns: { fieldKey: string; headerLabel: string }[];
    isDefault?: boolean;
  }): Promise<PayrollBuilderReportTemplateDto> => {
    const response = await api.post<PayrollBuilderReportTemplateDto>('/payroll-builder-report-templates', body);
    return response.data;
  },
  update: async (
    id: number,
    body: {
      name: string;
      statusFilter: string;
      layout: string;
      employeeFilters: unknown[];
      reportOptions: Record<string, unknown>;
      columns: { fieldKey: string; headerLabel: string }[];
      isDefault?: boolean;
    }
  ): Promise<PayrollBuilderReportTemplateDto> => {
    const response = await api.put<PayrollBuilderReportTemplateDto>(
      `/payroll-builder-report-templates/${id}`,
      body
    );
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/payroll-builder-report-templates/${id}`);
  },
};

//  Notification API
// ─────────────────────────────────────────────────────────────────────────────

export interface AppNotification {
  id: number;
  recipientEmployeeId: number;
  senderEmployeeId?: number;
  type: string;
  title: string;
  message: string;
  entityType?: string;
  entityId?: number;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  deepLinkWeb?: string;
  deepLinkMobile?: string;
}

export const notificationApi = {
  getAll: async (page = 1, pageSize = 20): Promise<{
    items: AppNotification[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const response = await api.get('/notifications', { params: { page, pageSize } });
    return response.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread-count');
    return response.data.count;
  },

  markAsRead: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/read`);
  },

  markAsUnread: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/unread`);
  },

  markAllAsRead: async (): Promise<void> => {
    await api.put('/notifications/read-all');
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`);
  },
};

export type DemoDataBackfillResult = {
  rangeStart?: string | null;
  rangeEnd?: string | null;
  employeesConsidered: number;
  shiftsInserted: number;
  attendanceInserted: number;
  timecardDaysInserted: number;
  weeksApproved: number;
  weeksPublished: number;
  openPunchesClosed: number;
  alreadyCurrent: boolean;
  message: string;
};

export const demoApi = {
  backfillUntilToday: async (): Promise<DemoDataBackfillResult> => {
    const response = await api.post('/demo/backfill-until-today', null, { timeout: 180000 });
    return response.data;
  },
};

export type QaUiTestStep = {
  id: string;
  title: string;
  status: 'pending' | 'running' | 'passed' | 'failed' | 'skipped';
  message: string;
};

export type QaUiTestRun = {
  runId: string;
  status: 'queued' | 'running' | 'passed' | 'failed';
  message: string;
  startedAt: string;
  finishedAt?: string | null;
  headless: boolean;
  frontendBaseUrl: string;
  steps: QaUiTestStep[];
};

export type QaUiTestCapabilities = {
  enabled: boolean;
  hasPassword: boolean;
  headless: boolean;
  frontendBaseUrl: string;
  email: string;
  tenant: string;
  blockedReason?: string | null;
};

export const qaUiTestApi = {
  capabilities: async (): Promise<QaUiTestCapabilities> => {
    const response = await api.get('/qa/ui-tests/capabilities');
    return response.data;
  },
  getRun: async (runId: string): Promise<QaUiTestRun> => {
    const response = await api.get(`/qa/ui-tests/${runId}`);
    return response.data;
  },
  start: async (password?: string): Promise<QaUiTestRun> => {
    try {
      const response = await api.post('/qa/ui-tests', password ? { password } : {}, { timeout: 20000 });
      return response.data;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: QaUiTestRun } };
      if (axiosErr.response?.status === 409 && axiosErr.response.data?.runId) {
        return axiosErr.response.data;
      }
      throw err;
    }
  },
};

export type PolicyDocumentCategoryCode =
  | 'HrPolicy'
  | 'HealthAndSafety'
  | 'CompanyPolicy'
  | 'DataAndIt'
  | 'Operations'
  | 'EmployeeHandbook'
  | 'Procedures'
  | 'Other';

export type PolicyDocumentAudienceRule = {
  allEmployees: boolean;
  locationIds: number[];
  positionIds: number[];
  departmentIds: number[];
  employmentTypeIds: number[];
  employeeIds: number[];
};

export type PolicyDocumentCompletion = {
  required: number;
  complete: number;
  outstanding: number;
  opened: number;
  notOpened: number;
  acknowledged: number;
  percent: number;
};

export type PolicyDocumentLibraryRow = {
  id: number;
  name: string;
  category: string;
  categoryLabel: string;
  ownerLabel?: string | null;
  status: string;
  displayStatus: string;
  versionLabel?: string | null;
  currentVersionId?: number | null;
  requiresAcknowledgement: boolean;
  audienceLabel: string;
  reviewDate?: string | null;
  effectiveDate?: string | null;
  acknowledged?: number;
  required?: number;
  outstanding?: number;
  percent?: number;
  updatedAt: string;
};

export type PolicyDocumentLibraryPage = {
  items: PolicyDocumentLibraryRow[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export type PolicyDocumentDetail = PolicyDocumentLibraryRow & {
  acknowledgementText: string;
  notifyOnPublish: boolean;
  firstReminderAfterDays?: number | null;
  repeatReminderEveryDays?: number | null;
  maxReminders: number;
  fileName?: string | null;
  contentType?: string | null;
  audienceAllEmployees: boolean;
  locationIds: number[];
  positionIds: number[];
  departmentIds: number[];
  employmentTypeIds: number[];
  employeeIds: number[];
  completion: PolicyDocumentCompletion;
  versions: Array<{
    id: number;
    versionLabel: string;
    fileName?: string | null;
    effectiveDate?: string | null;
    reviewDate?: string | null;
    requiresAcknowledgement: boolean;
    coverPriorAcknowledgements: boolean;
    publishedAt?: string | null;
    createdAt: string;
  }>;
  createdAt: string;
};

export type PolicyDocumentRecipientRow = {
  employeeId: number;
  employeeName: string;
  location?: string | null;
  distributedAt: string;
  openedAt?: string | null;
  acknowledgedAt?: string | null;
  status: string;
  stillInAudience: boolean;
  reminderCount: number;
};

export type MyDocumentRow = {
  id: number;
  name: string;
  category: string;
  categoryLabel: string;
  versionLabel: string;
  requiresAcknowledgement: boolean;
  status: string;
  distributedAt: string;
  openedAt?: string | null;
  acknowledgedAt?: string | null;
  outstanding: boolean;
};

export type MyDocumentDetail = MyDocumentRow & {
  ownerLabel?: string | null;
  currentVersionId?: number | null;
  fileName?: string | null;
  contentType?: string | null;
  effectiveDate?: string | null;
  reviewDate?: string | null;
  acknowledgementText: string;
  stillInAudience: boolean;
  history: Array<{
    versionId: number;
    versionLabel: string;
    status: string;
    distributedAt: string;
    openedAt?: string | null;
    acknowledgedAt?: string | null;
  }>;
};

export type UpsertPolicyDocumentPayload = {
  name: string;
  category: string;
  ownerLabel?: string | null;
  requiresAcknowledgement: boolean;
  notifyOnPublish: boolean;
  firstReminderAfterDays?: number | null;
  repeatReminderEveryDays?: number | null;
  maxReminders: number;
  versionLabel?: string | null;
  effectiveDate?: string | null;
  reviewDate?: string | null;
  audience: PolicyDocumentAudienceRule;
};

export const policyDocumentApi = {
  list: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    reviewDue?: boolean;
  }): Promise<PolicyDocumentLibraryPage> => {
    const response = await api.get('/policy-documents', {
      params: {
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        search: params?.search?.trim() || undefined,
        reviewDue: params?.reviewDue || undefined,
      },
    });
    return response.data;
  },
  summary: async (): Promise<{
    activeDocuments: number;
    awaitingAcknowledgement: number;
    fullyAcknowledged: number;
    dueForReview: number;
  }> => {
    const response = await api.get('/policy-documents/summary');
    return response.data;
  },
  get: async (id: number): Promise<PolicyDocumentDetail> => {
    const response = await api.get(`/policy-documents/${id}`);
    return response.data;
  },
  create: async (payload: UpsertPolicyDocumentPayload): Promise<PolicyDocumentDetail> => {
    const response = await api.post('/policy-documents', payload);
    return response.data;
  },
  update: async (id: number, payload: UpsertPolicyDocumentPayload): Promise<void> => {
    await api.put(`/policy-documents/${id}`, payload);
  },
  audiencePreview: async (audience: PolicyDocumentAudienceRule): Promise<{ count: number }> => {
    const response = await api.post('/policy-documents/audience-preview', audience);
    return response.data;
  },
  uploadFile: async (
    id: number,
    file: File,
    extras?: { versionLabel?: string; effectiveDate?: string; reviewDate?: string; requireNewAck?: boolean }
  ): Promise<PolicyDocumentDetail> => {
    const form = new FormData();
    form.append('file', file);
    if (extras?.versionLabel) form.append('versionLabel', extras.versionLabel);
    if (extras?.effectiveDate) form.append('effectiveDate', extras.effectiveDate);
    if (extras?.reviewDate) form.append('reviewDate', extras.reviewDate);
    if (extras?.requireNewAck != null) form.append('requireNewAck', String(extras.requireNewAck));
    const response = await api.post(`/policy-documents/${id}/file`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  publish: async (id: number): Promise<{ audienceCount: number; message: string }> => {
    const response = await api.post(`/policy-documents/${id}/publish`);
    return response.data;
  },
  archive: async (id: number): Promise<void> => {
    await api.post(`/policy-documents/${id}/archive`);
  },
  acknowledgements: async (
    id: number,
    filter?: string,
    locationId?: number | null
  ): Promise<{ summary: PolicyDocumentCompletion; items: PolicyDocumentRecipientRow[] }> => {
    const response = await api.get(`/policy-documents/${id}/acknowledgements`, {
      params: { filter, locationId: locationId || undefined },
    });
    return response.data;
  },
  exportAcknowledgements: async (id: number): Promise<Blob> => {
    const response = await api.get(`/policy-documents/${id}/acknowledgements/export`, { responseType: 'blob' });
    return response.data;
  },
  remind: async (id: number): Promise<{ reminded: number }> => {
    const response = await api.post(`/policy-documents/${id}/remind`);
    return response.data;
  },
  downloadFile: async (id: number, versionId?: number, disposition: 'inline' | 'attachment' = 'inline'): Promise<Blob> => {
    const response = await api.get(`/policy-documents/${id}/file`, {
      params: { versionId, disposition },
      responseType: 'blob',
    });
    return response.data;
  },
};

export const myDocumentApi = {
  list: async (): Promise<MyDocumentRow[]> => {
    const response = await api.get('/my-documents');
    return response.data;
  },
  get: async (id: number): Promise<MyDocumentDetail> => {
    const response = await api.get(`/my-documents/${id}`);
    return response.data;
  },
  downloadFile: async (id: number, disposition: 'inline' | 'attachment' = 'inline'): Promise<Blob> => {
    const response = await api.get(`/my-documents/${id}/file`, {
      params: { disposition },
      responseType: 'blob',
    });
    return response.data;
  },
  acknowledge: async (id: number): Promise<{ status: string; acknowledgedAt?: string; message?: string }> => {
    const response = await api.post(`/my-documents/${id}/acknowledge`, { confirmed: true });
    return response.data;
  },
};

export { api };
